<template>
  <div class="animated fadeIn page-shell">
    <div class="page-main">
      <header-padrao class="mb-2" icon="fa fa-clipboard" titulo="APONTAMENTO PARCIAL - PRODUÇÃO POR FÓRMULA">
        <template #subtitulo>
          <div class="d-flex align-items-center flex-wrap">
            <span class="mr-3">
              <i class="fa fa-industry mr-1 text-info"></i>
              Processo <strong class="text-white">{{ processo.ID || '--' }}</strong>
            </span>
            <span class="mr-3" v-if="processo.LOTE">
              <i class="fa fa-tags mr-1 text-warning"></i>
              Lote final <strong class="text-white">{{ processo.LOTE }}</strong>
            </span>
            <span v-if="processo.FORMULA_DESCRICAO" class="text-white-50">{{ processo.FORMULA_DESCRICAO }}</span>
          </div>
        </template>

        <template #acoes>
          <b-button v-if="processo.ID" variant="link" class="btn-glass btn-glass-secondary px-4 font-weight-bold" @click.stop.prevent="imprimirEspelhoApontamento">
            <i class="fa fa-file-text-o mr-1"></i> ESPELHO
          </b-button>
          <b-button v-if="podeFinalizarProcesso" variant="warning" pill class="font-weight-bold shadow-sm px-4" :disabled="isBusy" @click.stop.prevent="finalizarProcesso">
            <i class="fa fa-lock mr-1"></i> Finalizar processo
          </b-button>
          <b-button v-if="podeEditarApontamento" variant="link" class="btn-glass btn-glass-success px-4 font-weight-bold ml-2" :disabled="isBusy || salvando" @click.stop.prevent="salvar">
            <i class="fa fa-save mr-1"></i> Registrar apontamento
          </b-button>
          <b-button variant="light" pill class="ml-2 font-weight-bold shadow-sm px-4" @click.stop.prevent="voltar">
            <i class="fa fa-arrow-left mr-1"></i> Voltar
          </b-button>
        </template>
      </header-padrao>

      <b-overlay :show="isBusy" rounded="sm" opacity="0.65" variant="white" spinner-variant="primary">
        <b-row class="px-2">
          <b-col sm="12">
            <b-alert v-if="!podeEditarApontamento" show :variant="String(processo.SITUACAO || 'L') === 'F' ? 'success' : 'secondary'" class="mb-3">
              Processo em {{ getLabelSituacao(processo.SITUACAO).toLowerCase() }}. A tela permanece disponível apenas para consulta.
            </b-alert>

            <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
              <template #header>
                <div class="d-flex align-items-center">
                  <div class="bg-info text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                    <i class="fa fa-info"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 font-weight-bold">Resumo do Processo</h6>
                    <small class="text-muted">Revisão rápida dos acumulados antes do apontamento manual.</small>
                  </div>
                </div>
              </template>

              <b-row>
                <b-col lg="4" md="6" class="mb-3">
                  <div class="summary-card h-100">
                    <small class="modal-label-premium">Produto Final</small>
                    <h5 class="mb-1 font-weight-bold text-dark">{{ processo.PRODUTO_CODIGONOME || '---' }}</h5>
                    <small class="text-muted d-block">Centro de produção: {{ processo.CENTRO_PRODUCAO_CODIGONOME || '---' }}</small>
                    <small class="text-muted d-block">Lote final: {{ processo.LOTE || '---' }}</small>
                  </div>
                </b-col>
                <b-col lg="2" md="6" class="mb-3">
                  <div class="summary-card h-100 text-center">
                    <small class="modal-label-premium">Produção Prevista</small>
                    <h4 class="mb-0 font-weight-bold text-primary">{{ formatarNumero(processo.QUANT_PREVISTA) }}</h4>
                    <small class="text-muted">Meta total do processo</small>
                  </div>
                </b-col>
                <b-col lg="2" md="6" class="mb-3">
                  <div class="summary-card h-100 text-center">
                    <small class="modal-label-premium">Produção Acumulada</small>
                    <h4 class="mb-0 font-weight-bold text-success">{{ formatarNumero(processo.QUANT_PRODUZIDA) }}</h4>
                    <small class="text-muted">Já registrada no processo</small>
                  </div>
                </b-col>
                <b-col lg="2" md="6" class="mb-3">
                  <div class="summary-card h-100 text-center">
                    <small class="modal-label-premium">Perda Acumulada</small>
                    <h4 class="mb-0 font-weight-bold text-danger">{{ formatarNumero(processo.QUANT_PERDA) }}</h4>
                    <small class="text-muted">Total perdido até agora</small>
                  </div>
                </b-col>
                <b-col lg="2" md="6" class="mb-3">
                  <div class="summary-card h-100 text-center">
                    <small class="modal-label-premium">Sobra Acumulada</small>
                    <h4 class="mb-0 font-weight-bold text-warning">{{ formatarNumero(processo.QUANT_SOBRA) }}</h4>
                    <small class="text-muted">Total reaproveitado/devolvido</small>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
              <template #header>
                <div class="d-flex align-items-center">
                  <div class="bg-success text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                    <i class="fa fa-edit"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 font-weight-bold">Cabeçalho do Apontamento</h6>
                    <small class="text-muted">Informe produção parcial, sobra, perda e observações do lançamento manual.</small>
                  </div>
                </div>
              </template>

              <b-row>
                <b-col md="3">
                  <label class="modal-label-premium">Produção do Apontamento (Quantidade) *</label>
                  <vue-autonumeric v-model="formulario.QUANT_PRODUZIDA" :options="formatoQuantidade" :class="['form-control', 'rounded-pill', 'text-right', 'shadow-xs', 'bg-light', {'is-invalid': submitAttempted && !producaoCabecalhoValida}]" :disabled="true"></vue-autonumeric>
                  <small class="field-help-text">Calculado automaticamente com base no consumo.</small>
                </b-col>
                <b-col md="3">
                  <label class="modal-label-premium">Produção do Apontamento (Peças/Volumes)</label>
                  <vue-autonumeric v-model="formulario.PECAS_PRODUZIDA" :options="formatoPecas" :class="['form-control', 'rounded-pill', 'text-right', 'shadow-xs', {'is-invalid': submitAttempted && !producaoCabecalhoValida}]" :disabled="!podeEditarApontamento" @input="recalcularQuantidadePorPecas"></vue-autonumeric>
                  <small class="field-help-text">Informe a quantidade produzida em peças/volumes.</small>
                </b-col>
                <b-col md="3">
                  <label class="modal-label-premium">Sobra do Apontamento</label>
                  <vue-autonumeric v-model="formulario.QUANT_SOBRA" :options="formatoQuantidade" class="form-control rounded-pill text-right shadow-xs" :disabled="!podeEditarApontamento"></vue-autonumeric>
                  <small class="field-help-text">Quantidade devolvida ou mantida como sobra neste lançamento.</small>
                </b-col>
                <b-col md="3">
                  <label class="modal-label-premium">Perda do Apontamento</label>
                  <vue-autonumeric v-model="formulario.QUANT_PERDA" :options="formatoQuantidade" class="form-control rounded-pill text-right shadow-xs" :disabled="!podeEditarApontamento"></vue-autonumeric>
                  <small class="field-help-text">Quantidade perdida neste lançamento.</small>
                </b-col>
                <!-- ===== BLOCO: Transformar sobra em produto acabado ===== -->
                <b-col md="12" v-if="temSobra && podeEditarApontamento" class="mt-3">
                  <div class="sobra-produto-wrapper p-3 rounded border"
                       :class="formulario.SOBRA_TRANSFORMAR ? 'border-warning bg-warning-soft' : 'border-light bg-light'">
                    
                    <!-- Checkbox de ativação -->
                    <div class="d-flex align-items-center">
                      <b-form-checkbox 
                        v-model="formulario.SOBRA_TRANSFORMAR"
                        name="chk-sobra-produto"
                        switch
                        class="custom-switch-premium font-weight-bold mb-0"
                      >
                          <span class="ml-2">Transformar sobra em produto acabado</span>
                        </b-form-checkbox>
                      <i class="fa fa-recycle ml-2 text-warning" v-show="formulario.SOBRA_TRANSFORMAR"></i>
                    </div>
                    <small class="text-muted d-block mt-1 ml-4">
                      Marque para gerar entrada no estoque de um produto secundário a partir da sobra.
                    </small>

                    <!-- Campos condicionais -->
                    <b-row v-if="formulario.SOBRA_TRANSFORMAR" class="mt-3">
                      <b-col md="6">
                        <label class="modal-label-premium">Produto Acabado (Sobra) *</label>
                        <produto-pesquisa
                          :id-inicial="formulario.SOBRA_PRODUTO_ID || null"
                          place="Buscar produto acabado gerado pela sobra..."
                          id-modal="modal-produto-sobra"
                          @retornaId="setSobraProdutoId"
                          @retornaNome="setSobraProdutoNome"
                          @retornaCusto="setSobraProdutoCusto"
                          ref="produtoPesquisaSobra"
                        />
                        <small v-if="submitAttempted && formulario.SOBRA_TRANSFORMAR && !formulario.SOBRA_PRODUTO_ID"
                               class="text-danger">
                          Selecione o produto acabado gerado pela sobra.
                        </small>
                      </b-col>
                      <b-col md="3">
                        <label class="modal-label-premium">Lote do Produto (Sobra) *</label>
                        <b-form-input
                          v-model="formulario.SOBRA_LOTE"
                          maxlength="30"
                          class="rounded-pill shadow-xs text-uppercase"
                          placeholder="Ex: LOTE-SOBRA-001"
                          :class="{'is-invalid': submitAttempted && formulario.SOBRA_TRANSFORMAR && !formulario.SOBRA_LOTE}"
                        />
                        <small v-if="submitAttempted && formulario.SOBRA_TRANSFORMAR && !formulario.SOBRA_LOTE"
                               class="text-danger">
                          Informe o lote do produto gerado pela sobra.
                        </small>
                      </b-col>
                      <b-col md="3">
                        <label class="modal-label-premium">Quantidade da Sobra</label>
                        <vue-autonumeric 
                          :value="formulario.QUANT_SOBRA" 
                          :options="formatoQuantidade"
                          class="form-control rounded-pill text-right shadow-xs bg-light"
                          :disabled="true"
                        />
                        <small class="field-help-text">Valor espelhado do campo Sobra acima.</small>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </b-row>

              <b-row class="mt-3">
                 <b-col md="6">
                   <label class="modal-label-premium">Observação</label>
                   <b-form-input v-model="formulario.OBSERVACAO" maxlength="200" class="rounded-pill shadow-xs" :disabled="!podeEditarApontamento"></b-form-input>
                 </b-col>
              </b-row>
            </b-card>

            <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
              <template #header>
                <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                  <div class="d-flex align-items-center">
                    <div class="bg-primary text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                      <i class="fa fa-cubes"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 font-weight-bold">Consumo Real por Item</h6>
                      <small class="text-muted">Compare consumo previsto, consumo acumulado e consumo deste apontamento antes de salvar.</small>
                    </div>
                  </div>
                  <div class="text-md-right text-muted small">
                    Saldo exibido com prioridade para o saldo líquido disponível.
                  </div>
                </div>
              </template>

              <div v-if="podeEditarApontamento" class="rateio-hint mb-3">
                <i class="fa fa-info-circle mr-2"></i>
                Itens com lote exigem rateio completo antes de salvar.
              </div>

              <b-table
                :items="itens"
                :fields="camposItens"
                :tbody-tr-class="classeLinhaItem"
                small
                responsive
                hover
                show-empty
                empty-text="Nenhum item previsto para apontamento"
                class="mb-0 table-finance">
                <template #cell(PRODUTO_CODIGONOME)="row">
                  <div>
                    <div class="font-weight-bold">{{ row.item.PRODUTO_CODIGONOME }}</div>
                    <small class="text-muted">{{ row.item.CENTRO_ESTOQUE_CODIGONOME }}</small>
                    <div class="mt-2 d-flex align-items-center flex-wrap gap-1">
                      <b-badge v-if="row.item.OP_CONTROLA_LOTE === '1'" variant="warning" pill>
                        Controla lote
                      </b-badge>
                      <b-badge v-else variant="light" pill>
                        Não controla lote
                      </b-badge>
                      <b-badge v-if="podeEditarApontamento && row.item.OP_CONTROLA_LOTE === '1' && possuiMovimentoItem(row.item) && !possuiLotesValidos(row.item)" variant="danger" pill>
                        Rateio pendente
                      </b-badge>
                    </div>
                  </div>
                </template>

                <template #cell(ESTOQUE_ATUAL)="row">
                  <div class="text-right">
                    <div class="font-weight-bold">{{ formatarNumero(getSaldoQuantidadeItem(row.item)) }}</div>
                    <small class="text-muted d-block">Saldo disponível</small>
                    <small v-if="Number(row.item.QUANT_RESERVADA || 0) > 0" class="text-warning d-block">
                      Reservado: {{ formatarNumero(row.item.QUANT_RESERVADA) }}
                    </small>
                  </div>
                </template>

                <template #cell(PREVISTO)="row">
                  <div class="text-right">
                    <div>{{ formatarNumero(row.item.QUANT_PREVISTA) }}</div>
                    <small class="text-muted">Previsto</small>
                  </div>
                </template>

                <template #cell(APONTADO)="row">
                  <div class="text-right">
                    <div>{{ formatarNumero(row.item.QUANT_APONTADA) }}</div>
                    <small class="text-muted">Consumo acumulado</small>
                  </div>
                </template>

                <template #cell(CONSUMO_ATUAL)="row">
                  <div class="d-flex flex-column align-items-stretch">
                    <vue-autonumeric v-model="row.item.QUANT_CONSUMIDA" :options="formatoQuantidade" :class="classeCampoConsumo(row.item)" :disabled="!podeEditarApontamento" @input="recalcularProducao(row.item)"></vue-autonumeric>
                  </div>
                </template>



                <template #cell(LOTES)="row">
                  <div class="text-center">
                    <b-badge v-if="row.item.OP_CONTROLA_LOTE === '1'" :variant="getVariantLote(row.item)" pill class="mb-2">
                      {{ getTextoStatusLote(row.item) }}
                    </b-badge>
                    <div>
                      <b-button v-if="row.item.OP_CONTROLA_LOTE === '1' && podeEditarApontamento" :variant="possuiMovimentoItem(row.item) && !possuiLotesValidos(row.item) ? 'outline-danger' : 'outline-primary'" pill size="sm" class="px-3 font-weight-bold" @click.stop.prevent="abrirModalLotes(row.item)">
                        <i class="fa fa-layer-group mr-1"></i> {{ possuiMovimentoItem(row.item) ? 'Ratear lotes' : 'Selecionar lotes' }}
                      </b-button>
                      <div v-if="row.item.OP_CONTROLA_LOTE === '1'" class="small text-muted mt-1">
                        {{ getTextoAcaoLote(row.item) }}
                      </div>
                      <div v-if="podeEditarApontamento && submitAttempted && row.item.OP_CONTROLA_LOTE === '1' && possuiMovimentoItem(row.item) && !possuiLotesValidos(row.item)" class="small text-danger mt-1 font-weight-bold">
                        Rateio obrigatório para este item.
                      </div>
                      <span v-else class="text-muted small">Não controla lote</span>
                    </div>
                  </div>
                </template>
              </b-table>
            </b-card>

            <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
              <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                <div>
                  <h6 class="mb-1 font-weight-bold">Resumo deste Apontamento</h6>
                  <small class="text-muted">Bloco apenas visual para conferência rápida antes do registro.</small>
                </div>
                <div class="summary-strip mt-3 mt-lg-0">
                  <div class="summary-strip-item">
                    <small>Produção</small>
                    <strong class="text-success">{{ formatarNumero(resumoApontamento.producao) }}</strong>
                  </div>
                  <div class="summary-strip-item">
                    <small>Consumo total</small>
                    <strong class="text-primary">{{ formatarNumero(resumoApontamento.consumo) }}</strong>
                  </div>
                  <div class="summary-strip-item">
                    <small>Sobra</small>
                    <strong class="text-warning">{{ formatarNumero(resumoApontamento.sobra) }}</strong>
                  </div>
                  <div class="summary-strip-item">
                    <small>Perda</small>
                    <strong class="text-danger">{{ formatarNumero(resumoApontamento.perda) }}</strong>
                  </div>
                </div>
              </div>
            </b-card>

            <!-- CUSTOS REAIS -->
            <b-card v-if="processo.ID" class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
              <template #header>
                <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                  <div class="d-flex align-items-center">
                    <div class="bg-warning text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                      <i class="fa fa-dollar"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 font-weight-bold">Custos Operacionais Reais</h6>
                      <small class="text-muted">Despesas adicionais reais incorridas neste processo.</small>
                    </div>
                  </div>
                  <div>
                    <b-button v-if="podeEditarApontamento" variant="outline-warning" pill class="px-3" @click="abrirModalCustoReal()">
                      <i class="fa fa-plus mr-1"></i> Lançar Custo
                    </b-button>
                  </div>
                </div>
              </template>

              <b-overlay :show="carregandoCustos" rounded="sm" opacity="0.65" variant="white">
                <b-table
                  :items="custosReais"
                  :fields="[
                    { key: 'TIPO_ITEM', label: 'Tipo', class: 'align-middle text-center', thStyle: { width: '80px' } },
                    { key: 'DATA_LANCAMENTO', label: 'Data', class: 'align-middle' },
                    { key: 'NATUREZA_DESCRICAO', label: 'Natureza', class: 'align-middle font-weight-bold' },
                    { key: 'DESCRICAO_EXIBICAO', label: 'Produto / Detalhes', class: 'align-middle' },
                    { key: 'VALOR', label: 'Valor (R$)', class: 'align-middle text-right text-success font-weight-bold' },
                    { key: 'ACOES', label: 'Ações', class: 'align-middle text-center', thStyle: { width: '120px' } }
                  ]"
                  hover
                  small
                  responsive
                  show-empty
                  empty-text="Nenhum custo real registrado para este processo"
                  class="mb-0 table-finance"
                  @row-dblclicked="podeEditarApontamento ? abrirModalCustoReal($event) : null"
                >
                  <template #cell(TIPO_ITEM)="ctx">
                    <b-badge :variant="ctx.item.PRODUTO_ID ? 'info' : 'secondary'">{{ ctx.item.PRODUTO_ID ? 'Produto' : 'Manual' }}</b-badge>
                  </template>
                  <template #cell(DATA_LANCAMENTO)="ctx">{{ formatarDataHora(ctx.item.DATA_LANCAMENTO) }}</template>
                  <template #cell(DESCRICAO_EXIBICAO)="ctx">
                    <div v-if="ctx.item.PRODUTO_ID">
                      <span class="d-block font-weight-bold text-primary">{{ ctx.item.PRODUTO_CODIGONOME || ctx.item.PRODUTO_DESCRICAO || 'Produto sem descrição' }}</span>
                      <small class="text-muted" v-if="ctx.item.QUANTIDADE_EXECUTADA">{{ formatarNumero(ctx.item.QUANTIDADE_EXECUTADA) }} un x {{ formatarMoeda(ctx.item.VALOR_UNITARIO_APLICADO) }}</small>
                    </div>
                    <div v-else>-</div>
                  </template>
                  <template #cell(VALOR)="ctx">{{ formatarMoeda(ctx.item.VALOR) }}</template>
                  <template #cell(ACOES)="ctx">
                    <b-button v-if="podeEditarApontamento" variant="outline-primary" size="sm" pill class="mr-1" title="Editar" @click.stop="abrirModalCustoReal(ctx.item)">
                      <i class="fa fa-pencil"></i>
                    </b-button>
                    <b-button v-if="podeEditarApontamento" variant="outline-danger" size="sm" pill title="Excluir" @click.stop="excluirCustoReal(ctx.item.ID)">
                      <i class="fa fa-trash"></i>
                    </b-button>
                  </template>

                  <template #custom-foot="{ fields }">
                    <b-tr v-if="custosReais && custosReais.length > 0">
                      <b-td :colspan="fields.length - 2" class="text-right bg-light py-2 px-3 font-weight-bold">Total Acumulado (Custos Reais):</b-td>
                      <b-td class="text-right bg-light py-2 px-3 text-success font-weight-bold" style="font-size: 1rem;">
                        {{ formatarMoeda(custosReais.reduce((acc, it) => acc + (Number(it.VALOR)||0), 0)) }}
                      </b-td>
                      <b-td class="bg-light"></b-td>
                    </b-tr>
                  </template>
                </b-table>
              </b-overlay>
            </b-card>

            <b-card class="shadow-sm" :class="{'bg-dark border-secondary': isDark}">
              <template #header>
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <div class="bg-secondary text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                      <i class="fa fa-history"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 font-weight-bold">Histórico de Apontamentos</h6>
                      <small class="text-muted">Acompanhamento dos registros parciais já gravados no processo.</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-form-checkbox v-model="mostrarCancelados" name="check-button" switch class="custom-switch-premium text-muted small font-weight-bold">
                      Mostrar excluídos
                    </b-form-checkbox>
                  </div>
                </div>
              </template>

              <b-table :items="apontamentosFiltrados" :fields="camposApontamentos" :tbody-tr-class="row => String(row && row.OP_STATUS || '1') !== '1' ? 'linha-apontamento-cancelado' : ''" small responsive hover show-empty empty-text="Nenhum apontamento registrado" class="mb-0 table-finance">
                <template #cell(DATA_APONTAMENTO)="row">
                  {{ formatarDataHora(row.item.DATA_APONTAMENTO) }}
                </template>
                <template #cell(OP_STATUS_LABEL)="row">
                  <b-badge :variant="String(row.item.OP_STATUS || '1') === '1' ? 'success' : 'danger'" pill>
                    {{ row.item.OP_STATUS_LABEL || (String(row.item.OP_STATUS || '1') === '1' ? 'Ativo' : 'Cancelado') }}
                  </b-badge>
                </template>
                <template #cell(QUANT_PRODUZIDA)="row">
                  <div class="text-right">
                    <span class="d-block text-success font-weight-bold">{{ formatarNumero(row.item.QUANT_PRODUZIDA) }}</span>
                    <small class="text-muted">{{ formatarInteiro(row.item.PECAS_PRODUZIDA) }} pçs/vol.</small>
                  </div>
                </template>
                <template #cell(QUANT_SOBRA)="row">
                    <div class="text-right">
                      <span class="d-block text-warning font-weight-bold">{{ formatarNumero(row.item.QUANT_SOBRA) }}</span>
                      <small class="text-muted d-block text-truncate mt-1" style="max-width: 150px; margin-left: auto;" v-if="row.item.SOBRA_PRODUTO_CODIGONOME" :title="row.item.SOBRA_PRODUTO_CODIGONOME">
                        {{ row.item.SOBRA_PRODUTO_CODIGONOME }}
                      </small>
                      <small class="text-info d-block font-weight-bold" v-if="row.item.SOBRA_LOTE">
                        Lote: {{ row.item.SOBRA_LOTE }}
                      </small>
                    </div>
                  </template>
                <template #cell(QUANT_PERDA)="row">
                  <div class="text-right">
                    <span class="d-block text-danger font-weight-bold">{{ formatarNumero(row.item.QUANT_PERDA) }}</span>
                  </div>
                </template>
                <template #cell(actions)="row">
                  <div class="text-center d-flex justify-content-center gap-2 flex-wrap">
                    <b-button variant="soft-secondary" pill class="btn-icon-only shadow-sm" title="Espelho do apontamento" v-b-tooltip.hover.bottom @click.stop.prevent="imprimirEspelhoApontamentoHistorico(row.item)">
                      <i class="fa fa-file-text-o"></i>
                    </b-button>
                    <b-button variant="soft-primary" pill class="btn-icon-only shadow-sm" title="Ver itens" v-b-tooltip.hover.bottom @click.stop.prevent="abrirModalDetalhes(row.item)">
                      <i class="fa fa-eye"></i>
                    </b-button>
                    <b-button v-if="String(row.item.OP_STATUS || '1') === '1'" variant="soft-danger" pill class="btn-icon-only shadow-sm" title="Cancelar apontamento" v-b-tooltip.hover.bottom :disabled="!podeCancelarApontamento(row.item)" @click.stop.prevent="cancelarApontamento(row.item)">
                      <i class="fa fa-ban"></i>
                    </b-button>
                  </div>
                </template>
              </b-table>
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>

      <b-modal
        ref="modal-lotes"
        id="modal-lotes-producao-formula"
        hide-header
        hide-footer
        centered
        size="xl"
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        no-close-on-backdrop>
        <b-overlay :show="modalLotes.loading" rounded="sm" opacity="0.6" variant="white" spinner-variant="primary">
          <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;">
            <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-layer-group text-white"></i>
            </div>
            <div>
              <h5 class="mb-0 font-weight-bold text-white">Consumo por Lote</h5>
              <small class="text-white opacity-75">Distribua o consumo real entre os lotes disponíveis da matéria-prima.</small>
            </div>
            <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$bvModal.hide('modal-lotes-producao-formula')">
              <i class="fa fa-times fa-lg"></i>
            </b-button>
          </div>

          <div class="p-4 bg-white">
            <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded" v-if="modalLotes.item">
              <b-row align-v="center">
                <b-col md="4">
                  <small class="modal-label-premium">Matéria-Prima</small>
                  <h5 class="mb-0 font-weight-bold text-dark">{{ modalLotes.item.PRODUTO_CODIGONOME }}</h5>
                </b-col>
                <b-col md="3" class="border-left">
                  <small class="modal-label-premium">Centro de Estoque</small>
                  <h6 class="mb-0 font-weight-bold text-dark">{{ modalLotes.item.CENTRO_ESTOQUE_CODIGONOME || '---' }}</h6>
                </b-col>
                <b-col md="2" class="border-left">
                  <small class="modal-label-premium">Qtd. Alvo</small>
                  <h5 class="mb-0 font-weight-bold text-primary">{{ formatarNumero(modalLotes.item.QUANT_CONSUMIDA) }}</h5>
                </b-col>
                <b-col md="3" class="border-left text-right">
                  <small class="modal-label-premium">Lançado nos Lotes</small>
                  <h5 class="mb-0 font-weight-bold" :class="modalLotesValido ? 'text-success' : 'text-danger'">{{ formatarNumero(totalLotesModal.quantidade) }}</h5>
                </b-col>
              </b-row>
            </div>

            <b-table :items="modalLotes.lotes" :fields="camposLotes" small responsive hover show-empty empty-text="Nenhum lote disponível para este item" class="mb-0 table-finance">
              <template #cell(LOTE)="row">
                <div>
                  <div class="font-weight-bold">{{ row.item.LOTE }}</div>
                  <small class="text-muted">Origem {{ row.item.OP_ORIGEM || '--' }}</small>
                </div>
              </template>
              <template #cell(SALDO)="row">
                <div class="text-right">
                  <div>{{ formatarNumero(row.item.QUANT_SALDO) }}</div>
                  <small class="text-muted">Saldo bruto</small>
                </div>
              </template>
              <template #cell(OP_ORIGEM)="row">
                <span>{{ row.item.OP_ORIGEM || '--' }}</span>
              </template>
              <template #cell(VL_CUSTO_UNITARIO)="row">
                <span class="text-right d-block">{{ formatarNumero(row.item.VL_CUSTO_UNITARIO) }}</span>
              </template>
              <template #cell(CONSUMO)="row">
                <div class="d-flex flex-column align-items-stretch">
                  <vue-autonumeric v-model="row.item.QUANT_CONSUMIDA" :options="formatoQuantidade" class="form-control form-control-sm rounded-pill text-right shadow-xs mb-1" :disabled="!podeEditarApontamento"></vue-autonumeric>
                </div>
              </template>
              <template #cell(ACOES)="row">
                <div class="text-center">
                  <b-button variant="outline-info" size="sm" pill class="px-3" :disabled="!podeEditarApontamento" @click.stop.prevent="usarValorDoApontamento(row.item)">
                    Usar valor do apontamento
                  </b-button>
                </div>
              </template>
            </b-table>

            <div class="modal-footer-premium p-3 bg-light border-top mt-4 mx-n4 mb-n4 d-flex justify-content-between">
              <b-button variant="soft-danger" pill class="px-4 font-weight-bold" @click="$bvModal.hide('modal-lotes-producao-formula')">
                Cancelar
              </b-button>
              <b-button variant="success" pill class="px-5 font-weight-bold shadow-sm" :disabled="!podeEditarApontamento" @click="confirmarLotes">
                <i class="fa fa-check-circle mr-1"></i> Confirmar lotes
              </b-button>
            </div>
          </div>
        </b-overlay>
      </b-modal>

      <b-modal
        ref="modal-detalhes"
        id="modal-detalhes-apontamento"
        hide-header
        hide-footer
        centered
        size="xl"
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg">
        <b-overlay :show="modalDetalhes.loading" rounded="sm" opacity="0.6" variant="white" spinner-variant="primary">
          <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #198754 0%, #146c43 100%) !important;">
            <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-list text-white"></i>
            </div>
            <div>
              <h5 class="mb-0 font-weight-bold text-white">Itens do Apontamento</h5>
              <small class="text-white opacity-75">Detalhamento do consumo gravado no apontamento selecionado.</small>
            </div>
            <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$bvModal.hide('modal-detalhes-apontamento')">
              <i class="fa fa-times fa-lg"></i>
            </b-button>
          </div>

          <div class="p-4 bg-white">
            <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded" v-if="modalDetalhes.apontamento">
              <b-row align-v="center">
                <b-col md="4">
                  <small class="modal-label-premium">Apontamento</small>
                  <h5 class="mb-0 font-weight-bold text-dark">#{{ modalDetalhes.apontamento.ID }}</h5>
                </b-col>
                <b-col md="4" class="border-left">
                  <small class="modal-label-premium">Produção</small>
                  <h5 class="mb-0 font-weight-bold text-success">{{ formatarNumero(modalDetalhes.apontamento.QUANT_PRODUZIDA) }}</h5>
                </b-col>
                <b-col md="4" class="border-left">
                  <small class="modal-label-premium">Usuário</small>
                  <h5 class="mb-0 font-weight-bold text-primary">{{ modalDetalhes.apontamento.USUARIO_CADASTRO }}</h5>
                </b-col>
              </b-row>
              <b-row align-v="center" class="mt-3 pt-3 border-top" v-if="modalDetalhes.apontamento && modalDetalhes.apontamento.SOBRA_PRODUTO_ID">
                <b-col md="8">
                  <small class="modal-label-premium">Produto Gerado pela Sobra</small>
                  <h6 class="mb-0 font-weight-bold text-dark">{{ modalDetalhes.apontamento.SOBRA_PRODUTO_CODIGONOME }}</h6>
                </b-col>
                <b-col md="4" class="border-left">
                  <small class="modal-label-premium">Lote Gerado</small>
                  <h6 class="mb-0 font-weight-bold text-info">{{ modalDetalhes.apontamento.SOBRA_LOTE || '---' }}</h6>
                </b-col>
              </b-row>
            </div>

            <div v-if="modalDetalhes.itens.length" class="mb-3">
              <div class="detalhes-summary-strip">
                <div class="detalhes-summary-item">
                  <small>Itens lançados</small>
                  <strong>{{ modalDetalhes.itens.length }}</strong>
                </div>
                <div class="detalhes-summary-item">
                  <small>Consumo total</small>
                  <strong class="text-primary">{{ formatarNumero(modalDetalhesResumo.consumo) }}</strong>
                </div>
                <div class="detalhes-summary-item">
                  <small>Sobra total</small>
                  <strong class="text-warning">{{ formatarNumero(modalDetalhesResumo.sobra) }}</strong>
                </div>
                <div class="detalhes-summary-item">
                  <small>Perda total</small>
                  <strong class="text-danger">{{ formatarNumero(modalDetalhesResumo.perda) }}</strong>
                </div>
                <div class="detalhes-summary-item">
                  <small>Custo total</small>
                  <strong>{{ formatarNumero(modalDetalhesResumo.custo) }}</strong>
                </div>
              </div>
            </div>

            <div v-if="!modalDetalhes.itens.length" class="text-center py-4 text-muted border rounded bg-light">
              Nenhum item registrado
            </div>

            <div v-else>
              <div v-for="item in modalDetalhes.itens" :key="item.ID" class="detalhe-item-card mb-3">
                <div class="detalhe-item-header">
                  <div>
                    <small class="modal-label-premium mb-1">Item</small>
                    <h6 class="mb-1 font-weight-bold text-dark">{{ item.PRODUTO_CODIGONOME }}</h6>
                    <small class="text-muted d-block">{{ item.CENTRO_ESTOQUE_CODIGONOME }}</small>
                  </div>
                  <div class="detalhe-item-badge-wrap">
                    <b-badge v-if="item.LOTES && item.LOTES.length" variant="info" pill>
                      {{ item.LOTES.length }} lote(s)
                    </b-badge>
                    <b-badge v-else variant="light" pill>
                      Sem lote vinculado
                    </b-badge>
                  </div>
                </div>

                <div class="detalhe-item-metrics">
                  <div class="detalhe-metric-box">
                    <small>Consumido</small>
                    <strong class="text-primary">{{ formatarNumero(item.QUANT_CONSUMIDA) }}</strong>
                  </div>
                  <div class="detalhe-metric-box">
                    <small>Sobra</small>
                    <strong class="text-warning">{{ formatarNumero(item.QUANT_SOBRA) }}</strong>
                  </div>
                  <div class="detalhe-metric-box">
                    <small>Perda</small>
                    <strong class="text-danger">{{ formatarNumero(item.QUANT_PERDA) }}</strong>
                  </div>
                  <div class="detalhe-metric-box">
                    <small>Custo total</small>
                    <strong>{{ formatarNumero(item.CUSTO_TOTAL) }}</strong>
                  </div>
                </div>

                <div v-if="item.LOTES && item.LOTES.length" class="detalhe-lotes-box mt-3">
                  <div class="d-flex align-items-center mb-2">
                    <i class="fa fa-tags text-primary mr-2"></i>
                    <strong>Lotes consumidos do item</strong>
                  </div>
                  <b-table :items="item.LOTES" :fields="camposDetalhesLotes" small responsive hover class="mb-0 nested-lotes-table">
                    <template #cell(QUANT_CONSUMIDA)="loteRow">
                      <span class="text-right d-block">{{ formatarNumero(loteRow.item.QUANT_CONSUMIDA) }}</span>
                    </template>
                    <template #cell(CUSTO_UNITARIO)="loteRow">
                      <span class="text-right d-block">{{ formatarNumero(loteRow.item.CUSTO_UNITARIO) }}</span>
                    </template>
                    <template #cell(CUSTO_TOTAL)="loteRow">
                      <span class="text-right d-block">{{ formatarNumero(loteRow.item.CUSTO_TOTAL) }}</span>
                    </template>
                  </b-table>
                </div>

                <div v-else class="detalhe-sem-lote mt-3">
                  <i class="fa fa-info-circle mr-2 text-muted"></i>
                  Item sem rateio de lote vinculado neste apontamento.
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </b-modal>

      <!-- Modal Custo Real - Premium -->
      <b-modal
        id="modal-custo-real"
        v-model="modalCustoReal.show"
        hide-header
        hide-footer
        centered
        no-close-on-backdrop
        size="lg"
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        @hidden="resetModalCustoReal"
      >
        <b-overlay :show="modalCustoReal.loading" rounded="sm" opacity="0.6" variant="white" spinner-variant="warning">
          <!-- Header Premium -->
          <div class="modal-header-premium p-3 d-flex align-items-center" style="background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%) !important;">
            <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-money text-white"></i>
            </div>
            <div>
              <h5 class="mb-0 font-weight-bold text-white">{{ modalCustoReal.item.ID ? 'Editar Custo Real' : 'Lançar Custo Real' }}</h5>
              <small class="text-white opacity-75">Registre um custo operacional real para este processo.</small>
            </div>
            <b-button variant="link" class="ml-auto text-white p-0 opacity-75" :disabled="modalCustoReal.loading" @click="resetModalCustoReal">
              <i class="fa fa-times fa-lg"></i>
            </b-button>
          </div>

          <!-- Body -->
          <div class="p-4 bg-white">

            <!-- Tipo -->
            <div class="mb-3">
              <label class="modal-label-premium">Tipo de Custo</label>
              <b-form-radio-group
                v-model="modalCustoReal.tipoItem"
                :options="[{ text: 'Manual', value: 'M' }, { text: 'Produto / Insumo', value: 'P' }]"
                buttons
                button-variant="outline-warning"
                class="w-100"
                @change="limparProdutoModalCustoReal"
              />
            </div>

            <!-- Produto (apenas tipo P) -->
            <div v-if="modalCustoReal.tipoItem === 'P'" class="mb-3">
              <label class="modal-label-premium">Produto / Insumo *</label>
              <bens-pesquisa
                :id-inicial="modalCustoReal.item.PRODUTO_ID || null"
                place="Buscar produto do almoxarifado..."
                label-botao="..."
                @retornaId="setProdutoId"
                @retornaNome="setProdutoNome"
                @retornaCusto="setProdutoCusto"
                ref="bensPesquisaModal"
              />
              <small v-if="modalCustoReal.enviado && modalCustoReal.tipoItem === 'P' && !modalCustoReal.item.PRODUTO_ID" class="text-danger">Selecione um produto.</small>
            </div>

            <!-- Quantidade e Valor Unitário (tipo P) -->
            <b-row v-if="modalCustoReal.tipoItem === 'P'" class="mb-3">
              <b-col md="6">
                <label class="modal-label-premium">Quantidade *</label>
                <vue-autonumeric
                  v-model="modalCustoReal.item.QUANTIDADE_EXECUTADA"
                  :options="formatoQuantidade"
                  class="form-control rounded-pill text-right shadow-xs"
                  :class="{'is-invalid': modalCustoReal.enviado && modalCustoReal.tipoItem === 'P' && !(Number(modalCustoReal.item.QUANTIDADE_EXECUTADA) > 0)}"
                />
              </b-col>
              <b-col md="6">
                <label class="modal-label-premium">
                  Valor Unitário (R$)
                  <small class="text-muted font-weight-normal ml-1">(custo do almoxarifado)</small>
                </label>
                <vue-autonumeric
                  v-model="modalCustoReal.item.VALOR_UNITARIO_APLICADO"
                  :options="formatoMoedaNumerico"
                  class="form-control rounded-pill text-right shadow-xs bg-light"
                  :disabled="true"
                />
                <small class="text-muted">Preenchido automaticamente ao selecionar o produto.</small>
              </b-col>
            </b-row>

            <!-- Natureza -->
            <div class="mb-3">
              <label class="modal-label-premium">Natureza do Custo *</label>
              <b-form-select
                v-model="modalCustoReal.item.NATUREZA_ID"
                :options="opcoesNaturezas"
                class="rounded-pill shadow-xs"
                :class="{'is-invalid': modalCustoReal.enviado && !modalCustoReal.item.NATUREZA_ID}"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>Selecione uma natureza...</b-form-select-option>
                </template>
              </b-form-select>
              <small v-if="modalCustoReal.enviado && !modalCustoReal.item.NATUREZA_ID" class="text-danger">Campo obrigatório.</small>
            </div>

            <!-- Data de Lançamento -->
            <div class="mb-3">
              <label class="modal-label-premium">Data de Lançamento *</label>
              <cec-input-data
                v-model="modalCustoReal.item.DATA_LANCAMENTO"
                :state="modalCustoReal.enviado && !modalCustoReal.item.DATA_LANCAMENTO ? false : null"
                placeholder-text="DD/MM/AAAA"
                input-class="shadow-xs"
              />
              <small v-if="modalCustoReal.enviado && !modalCustoReal.item.DATA_LANCAMENTO" class="text-danger">Campo obrigatório.</small>
            </div>

            <!-- Valor do Custo (tipo M) / Total calculado (tipo P) -->
            <div class="mb-3">
              <label class="modal-label-premium">
                {{ modalCustoReal.tipoItem === 'P' ? 'Valor Total Calculado (R$)' : 'Valor do Custo (R$) *' }}
              </label>
              <vue-autonumeric
                v-model="valorTotalModalCustoReal"
                :options="formatoMoedaNumerico"
                class="form-control rounded-pill text-right shadow-xs font-weight-bold"
                :class="[
                  modalCustoReal.tipoItem === 'P' ? 'text-warning bg-light' : '',
                  {'is-invalid': modalCustoReal.enviado && modalCustoReal.tipoItem === 'M' && !(Number(valorTotalModalCustoReal) > 0)}
                ]"
                :disabled="modalCustoReal.tipoItem === 'P'"
              />
              <small v-if="modalCustoReal.tipoItem === 'P'" class="text-muted">Calculado automaticamente: Quantidade × Valor Unitário.</small>
              <small v-if="modalCustoReal.enviado && modalCustoReal.tipoItem === 'M' && !(Number(valorTotalModalCustoReal) > 0)" class="text-danger">Informe um valor maior que zero.</small>
            </div>
          </div>

          <!-- Footer Premium -->
          <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-between">
            <b-button variant="soft-danger" pill class="px-4 font-weight-bold" :disabled="modalCustoReal.loading" @click="resetModalCustoReal">
              Cancelar
            </b-button>
            <b-button variant="warning" pill class="px-5 font-weight-bold shadow-sm" :disabled="modalCustoReal.loading" @click="salvarCustoReal">
              <b-spinner small v-if="modalCustoReal.loading" class="mr-1"></b-spinner>
              <i class="fa fa-save mr-1" v-else></i>
              {{ modalCustoReal.loading ? 'Salvando...' : (modalCustoReal.item.ID ? 'Salvar Alterações' : 'Lançar Custo') }}
            </b-button>
          </div>
        </b-overlay>
      </b-modal>


    </div>
  </div>
</template>

<script>
import axios from 'axios'
import HeaderPadrao from '@/components/Cec/HeaderPadrao.vue'
import VueAutonumeric from 'vue-autonumeric'
import BensPesquisa from '@/views/pesquisa/BensPesquisa.vue'
import ProdutoPesquisa from '@/views/pesquisa/ProdutoPesquisa.vue'
import CecInputData from '@/components/Cec/CecInputData.vue'

function escapeHtml(valor) {
  return String(valor == null ? '' : valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatarDataHoraRelatorio() {
  return new Date().toLocaleString('pt-BR')
}

function novoFormulario(processoId) {
  return {
    PRD_FORM_PROC_ID: processoId || null,
    QUANT_PRODUZIDA: 0,
    PECAS_PRODUZIDA: 0,
    QUANT_SOBRA: 0,
    PECAS_SOBRA: 0,
    QUANT_PERDA: 0,
    PECAS_PERDA: 0,
    OBSERVACAO: '',
      SOBRA_TRANSFORMAR: false,
      SOBRA_PRODUTO_ID: null,
      SOBRA_PRODUTO_CODIGONOME: '',
      SOBRA_PRODUTO_CUSTO: 0,
      SOBRA_LOTE: ''
    }
  }

function novoItem(item) {
  return {
    ...item,
    QUANT_CONSUMIDA: 0,
    PECAS_CONSUMIDAS: 0,
    QUANT_SOBRA_ATUAL: 0,
    PECAS_SOBRA_ATUAL: 0,
    QUANT_PERDA_ATUAL: 0,
    PECAS_PERDA_ATUAL: 0,
    LOTES: []
  }
}

export default {
  name: 'ProducaoFormulaApontamentoCadastro',
  components: {
    HeaderPadrao,
    VueAutonumeric,
    BensPesquisa,
    ProdutoPesquisa,
    CecInputData
  },
  data() {
    return {
      isBusy: false,
      salvando: false,
      submitAttempted: false,
      empresaPreferencial: null,
      processo: {},
      itens: [],
      apontamentos: [],
      formulario: novoFormulario(null),
      formatoQuantidade: {
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        decimalCharacterAlternative: '.',
        roundingMethod: 'U',
        decimalPlaces: '3',
        minimumValue: '0'
      },
      formatoPecas: {
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        decimalCharacterAlternative: '.',
        roundingMethod: 'U',
        decimalPlaces: '0',
        minimumValue: '0'
      },
      camposItens: [
        { key: 'PRODUTO_CODIGONOME', label: 'Matéria-Prima' },
        { key: 'ESTOQUE_ATUAL', label: 'Saldo Líquido Disponível', class: 'text-right', thStyle: { width: '170px' } },
        { key: 'PREVISTO', label: 'Consumo Previsto', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'APONTADO', label: 'Consumido Acumulado', class: 'text-right', thStyle: { width: '140px' } },
        { key: 'CONSUMO_ATUAL', label: 'Consumo neste Apontamento', thStyle: { width: '180px' } },
        { key: 'LOTES', label: 'Lotes', class: 'text-center', thStyle: { width: '140px' } }
      ],
      camposApontamentos: [
        { key: 'ID', label: 'ID', class: 'text-center', thStyle: { width: '70px' } },
        { key: 'DATA_APONTAMENTO', label: 'Data', thStyle: { width: '150px' } },
        { key: 'USUARIO_CADASTRO', label: 'Usuário', thStyle: { width: '140px' } },
        { key: 'OP_STATUS_LABEL', label: 'Status', class: 'text-center', thStyle: { width: '110px' } },
        { key: 'QUANT_PRODUZIDA', label: 'Produção do Apontamento', class: 'text-right', thStyle: { width: '150px' } },
        { key: 'QUANT_SOBRA', label: 'Sobra do Apontamento', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'QUANT_PERDA', label: 'Perda do Apontamento', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'TOTAL_ITENS', label: 'Total de Itens', class: 'text-center', thStyle: { width: '100px' } },
        { key: 'actions', label: 'Ações', class: 'text-center', thStyle: { width: '160px' } }
      ],
      camposLotes: [
        { key: 'LOTE', label: 'Lote' },
        { key: 'SALDO', label: 'Saldo', class: 'text-right', thStyle: { width: '140px' } },
        { key: 'OP_ORIGEM', label: 'Origem', thStyle: { width: '90px' } },
        { key: 'VL_CUSTO_UNITARIO', label: 'Custo Unit.', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'CONSUMO', label: 'Consumo', thStyle: { width: '170px' } },
        { key: 'ACOES', label: 'Ações', class: 'text-center', thStyle: { width: '180px' } }
      ],
      camposDetalhes: [
        { key: 'PRODUTO_CODIGONOME', label: 'Item' },
        { key: 'CENTRO_ESTOQUE_CODIGONOME', label: 'Centro' },
        { key: 'QUANT_CONSUMIDA', label: 'Consumido', class: 'text-right', thStyle: { width: '100px' } },
        { key: 'CUSTO_TOTAL', label: 'Custo Total', class: 'text-right', thStyle: { width: '120px' } }
      ],
      camposDetalhesLotes: [
        { key: 'LOTE', label: 'Lote' },
        { key: 'QUANT_CONSUMIDA', label: 'Quantidade', class: 'text-right', thStyle: { width: '110px' } },
        { key: 'PECAS_CONSUMIDAS', label: 'Peças', class: 'text-right', thStyle: { width: '90px' } },
        { key: 'CUSTO_UNITARIO', label: 'Custo Unit.', class: 'text-right', thStyle: { width: '110px' } },
        { key: 'CUSTO_TOTAL', label: 'Custo Total', class: 'text-right', thStyle: { width: '110px' } }
      ],
      modalLotes: {
        loading: false,
        itemId: null,
        item: null,
        lotes: []
      },
      modalDetalhes: {
        loading: false,
        apontamento: null,
        itens: []
      },
      carregandoCustos: false,
      custosReais: [],
      opcoesNaturezas: [],
      formatoMoedaNumerico: {
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        decimalCharacterAlternative: '.',
        currencySymbol: '',
        currencySymbolPlacement: 's',
        roundingMethod: 'U',
        minimumValue: '0',
        decimalPlaces: '2'
      },
      modalCustoReal: {
        show: false,
        loading: false,
        tipoItem: 'M',
        item: { ID: null, NATUREZA_ID: null, VALOR: 0, DATA_LANCAMENTO: new Date().toISOString().slice(0, 10), PRODUTO_ID: null, PRODUTO_CODIGONOME: '', QUANTIDADE_EXECUTADA: 0, VALOR_UNITARIO_APLICADO: 0, VALOR_TOTAL: 0 },
        enviado: false
      },
      mostrarCancelados: false
    }
  },
  computed: {
      temSobra() {
        return Number(this.formulario.QUANT_SOBRA || 0) > 0
      },
    isDark() {
      return this.$store.getters['tema/IS_DARK']
    },
    podeEditarApontamento() {
      return String(this.processo.SITUACAO || 'L') === 'L'
    },
    podeFinalizarProcesso() {
      return this.podeEditarApontamento && this.apontamentosAtivos.length > 0
    },
    producaoCabecalhoValida() {
      return Number(this.formulario.QUANT_PRODUZIDA || 0) > 0 || Number(this.formulario.PECAS_PRODUZIDA || 0) > 0
    },
    apontamentosAtivos() {
      return (this.apontamentos || []).filter(item => String(item.OP_STATUS || '1') === '1')
    },
    apontamentosCancelados() {
      return (this.apontamentos || []).filter(item => String(item.OP_STATUS || '1') !== '1')
    },
    temItensComMovimento() {
      return this.itens.some(item => this.possuiMovimentoItem(item))
    },
    itensComMovimentoAtual() {
      return this.itens.filter(item => this.possuiMovimentoItem(item))
    },
    resumoApontamento() {
      return {
        producao: Number(this.formulario.QUANT_PRODUZIDA || 0),
        consumo: this.itens.reduce((total, item) => total + Number(item.QUANT_CONSUMIDA || 0), 0),
        sobra: Number(this.formulario.QUANT_SOBRA || 0) + this.itens.reduce((total, item) => total + Number(item.QUANT_SOBRA_ATUAL || 0), 0),
        perda: Number(this.formulario.QUANT_PERDA || 0) + this.itens.reduce((total, item) => total + Number(item.QUANT_PERDA_ATUAL || 0), 0)
      }
    },
    totalLotesModal() {
      return {
        quantidade: this.modalLotes.lotes.reduce((total, lote) => total + Number(lote.QUANT_CONSUMIDA || 0), 0),
        pecas: this.modalLotes.lotes.reduce((total, lote) => total + Number(lote.PECAS_CONSUMIDAS || 0), 0)
      }
    },
    modalLotesValido() {
      if (!this.modalLotes.item) return false
      const quantAlvo = Number(this.modalLotes.item.QUANT_CONSUMIDA || 0)
      const pecasAlvo = Number(this.modalLotes.item.PECAS_CONSUMIDAS || 0)
      const quantAtual = Number(this.totalLotesModal.quantidade || 0)
      const pecasAtual = Number(this.totalLotesModal.pecas || 0)

      if (quantAlvo > 0 && Math.abs(quantAtual - quantAlvo) > 0.0005) return false
      if (pecasAlvo > 0 && pecasAtual !== pecasAlvo) return false
      return quantAlvo > 0 || pecasAlvo > 0
    },
    modalDetalhesResumo() {
      return {
        consumo: this.modalDetalhes.itens.reduce((total, item) => total + Number(item.QUANT_CONSUMIDA || 0), 0),
        sobra: this.modalDetalhes.itens.reduce((total, item) => total + Number(item.QUANT_SOBRA || 0), 0),
        perda: this.modalDetalhes.itens.reduce((total, item) => total + Number(item.QUANT_PERDA || 0), 0),
        custo: this.modalDetalhes.itens.reduce((total, item) => total + Number(item.CUSTO_TOTAL || 0), 0)
      }
    },
    apontamentosAtivosOrdenados() {
      return (this.apontamentos || []).filter(item => String(item.OP_STATUS || '1') === '1')
    },
    valorTotalModalCustoReal: {
      get() {
        if (this.modalCustoReal.tipoItem === 'P') {
          return (Number(this.modalCustoReal.item.QUANTIDADE_EXECUTADA) || 0) * (Number(this.modalCustoReal.item.VALOR_UNITARIO_APLICADO) || 0)
        }
        return this.modalCustoReal.item.VALOR
      },
      set(val) {
        if (this.modalCustoReal.tipoItem === 'M') {
          this.modalCustoReal.item.VALOR = val
        }
      }
    },
    resumoEspelhoApontamento() {
      const itensMovimento = this.itensComMovimentoAtual
      const itensComLote = itensMovimento.filter(item => String(item.OP_CONTROLA_LOTE || '0') === '1')
      const itensLotePendentes = itensComLote.filter(item => !this.possuiLotesValidos(item))
      const saldoAposApontamento = Number(this.processo.QUANT_PREVISTA || 0) - Number(this.processo.QUANT_PRODUZIDA || 0) - Number(this.formulario.QUANT_PRODUZIDA || 0)

      return {
        itensMovimento: itensMovimento.length,
        itensComLote: itensComLote.length,
        itensLotePendentes: itensLotePendentes.length,
        itensSemLote: Math.max(0, itensMovimento.length - itensComLote.length),
        lotesUtilizados: itensMovimento.reduce((total, item) => total + (item.LOTES || []).length, 0),
        custoMovimento: itensMovimento.reduce((total, item) => total + this.calcularCustoMovimentoItem(item), 0),
        saldoAposApontamento,
        saldoAposApontamentoPositivo: Math.max(0, saldoAposApontamento),
        excedenteApontamento: Math.max(0, saldoAposApontamento * -1)
      }
    },
    apontamentosFiltrados() {
      if (this.mostrarCancelados) return this.apontamentos
      return (this.apontamentos || []).filter(a => String(a.OP_STATUS || '1') === '1')
    }
  },
  async mounted() {
    await this.carregarTela()
  },
  watch: {
      'formulario.QUANT_SOBRA'(novoValor) {
        if (Number(novoValor || 0) <= 0) {
          this.formulario.SOBRA_TRANSFORMAR = false
          this.limparCamposSobraProduto()
        }
      },
      'formulario.SOBRA_TRANSFORMAR'(marcado) {
        if (!marcado) {
          this.limparCamposSobraProduto()
        }
      }
    },
    methods: {
      limparCamposSobraProduto() {
        this.formulario.SOBRA_PRODUTO_ID = null
        this.formulario.SOBRA_PRODUTO_CODIGONOME = ''
        this.formulario.SOBRA_PRODUTO_CUSTO = 0
        this.formulario.SOBRA_LOTE = ''
      },
      setSobraProdutoId(id) {
        this.formulario.SOBRA_PRODUTO_ID = id
      },
      setSobraProdutoNome(nome) {
        this.formulario.SOBRA_PRODUTO_CODIGONOME = nome || ''
      },
      setSobraProdutoCusto(custo) {
        this.formulario.SOBRA_PRODUTO_CUSTO = Number(custo || 0)
      },
    voltar() {
      this.$router.push('/producao/formula-processo/listagem')
    },
    formatarNumero(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatarInteiro(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
    },
    formatarMoeda(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    async obterEmpresaPreferencial() {
      if (this.empresaPreferencial) return this.empresaPreferencial
      const { data } = await this.$http.get('/api/protected/conferenciaFinanceira/empresa-preferencial')
      this.empresaPreferencial = data || {}
      return this.empresaPreferencial
    },
    montarGraficoPizzaHtml(titulo, slices, textoCentro = '') {
      const total = slices.reduce((acc, item) => acc + Number(item.value || 0), 0)
      let acumulado = 0
      const gradiente = total > 0
        ? slices.map(item => {
          const percentual = Number(item.value || 0) / total * 100
          const inicio = acumulado
          const fim = acumulado + percentual
          acumulado = fim
          return `${item.color} ${inicio.toFixed(2)}% ${fim.toFixed(2)}%`
        }).join(', ')
        : '#dfe6eb 0% 100%'

      const legenda = slices.map(item => `
        <div class="pie-legend-item">
          <div class="pie-legend-label">
            <span class="pie-dot" style="background:${item.color};"></span>
            <span>${escapeHtml(item.label)}</span>
          </div>
          <strong>${escapeHtml(item.formatted)}</strong>
        </div>
      `).join('')

      return `
        <div class="pie-card">
          <div class="pie-title">${escapeHtml(titulo)}</div>
          <div class="pie-row">
            <div class="pie-chart" style="background: conic-gradient(${gradiente});">
              <div class="pie-center">${escapeHtml(textoCentro)}</div>
            </div>
            <div class="pie-legend">${legenda}</div>
          </div>
        </div>
      `
    },
    obterBaseCusto(quantidade, pecas) {
      const quantidadeNormalizada = Number(quantidade || 0)
      const pecasNormalizadas = Number(pecas || 0)
      return quantidadeNormalizada > 0 ? quantidadeNormalizada : pecasNormalizadas
    },
    getCustoUnitarioItem(item) {
      return Number(item?.CUSTO_UNIT_PREV || item?.VL_CUSTO || 0)
    },
    calcularCustoMovimentoItem(item) {
      if (!item) return 0

      if (String(item.OP_CONTROLA_LOTE || '0') === '1' && (item.LOTES || []).length > 0) {
        return (item.LOTES || []).reduce((total, lote) => {
          const baseCusto = this.obterBaseCusto(lote.QUANT_CONSUMIDA, lote.PECAS_CONSUMIDAS)
          const custoUnitario = Number(lote.CUSTO_UNITARIO || lote.VL_CUSTO_UNITARIO || 0)
          return total + (baseCusto * custoUnitario)
        }, 0)
      }

      const baseCusto = this.obterBaseCusto(item.QUANT_CONSUMIDA, item.PECAS_CONSUMIDAS)
      return baseCusto * this.getCustoUnitarioItem(item)
    },
    getItensComMovimentoApontamento(itens = []) {
      return (itens || []).filter(item => {
        return Number(item.QUANT_CONSUMIDA || 0) > 0 ||
          Number(item.PECAS_CONSUMIDAS || 0) > 0 ||
          Number(item.QUANT_SOBRA || item.QUANT_SOBRA_ATUAL || 0) > 0 ||
          Number(item.PECAS_SOBRA || item.PECAS_SOBRA_ATUAL || 0) > 0 ||
          Number(item.QUANT_PERDA || item.QUANT_PERDA_ATUAL || 0) > 0 ||
          Number(item.PECAS_PERDA || item.PECAS_PERDA_ATUAL || 0) > 0
      })
    },
    getResumoEspelhoApontamentoHistorico(apontamento, itens = []) {
      const itensMovimento = this.getItensComMovimentoApontamento(itens)
      const itensComLote = itensMovimento.filter(item => String(item.OP_CONTROLA_LOTE || '0') === '1')
      const itensLotePendentes = itensComLote.filter(item => !this.possuiLotesValidos(item))
      const apontamentosAtivos = this.apontamentosAtivosOrdenados
      const indiceApontamento = apontamentosAtivos.findIndex(item => Number(item.ID) === Number(apontamento.ID))
      const apontamentosMaisAntigos = indiceApontamento >= 0 ? apontamentosAtivos.slice(indiceApontamento + 1) : []
      const producaoAntes = apontamentosMaisAntigos.reduce((total, item) => total + Number(item.QUANT_PRODUZIDA || 0), 0)
      const producaoAtual = Number(apontamento.QUANT_PRODUZIDA || 0)
      const saldoAposApontamento = Number(this.processo.QUANT_PREVISTA || 0) - producaoAntes - (String(apontamento.OP_STATUS || '1') === '1' ? producaoAtual : 0)

      return {
        itensMovimento: itensMovimento.length,
        itensComLote: itensComLote.length,
        itensLotePendentes: itensLotePendentes.length,
        itensSemLote: Math.max(0, itensMovimento.length - itensComLote.length),
        lotesUtilizados: itensMovimento.reduce((total, item) => total + (item.LOTES || []).length, 0),
        custoMovimento: itensMovimento.reduce((total, item) => total + Number(item.CUSTO_TOTAL || this.calcularCustoMovimentoItem(item)), 0),
        producaoAntes,
        producaoDepois: producaoAntes + (String(apontamento.OP_STATUS || '1') === '1' ? producaoAtual : 0),
        saldoAposApontamento,
        saldoAposApontamentoPositivo: Math.max(0, saldoAposApontamento),
        excedenteApontamento: Math.max(0, saldoAposApontamento * -1)
      }
    },
    montarHtmlEspelhoApontamento(empresa) {
      const nomeUsuario = window.localStorage.getItem('nomeusuario') || window.localStorage.getItem('login') || 'sistema'
      const itensMovimento = this.itensComMovimentoAtual
      const resumo = this.resumoEspelhoApontamento
      const historicoRecente = (this.apontamentos || []).slice(0, 5)

      const graficoResultadoApontamento = this.montarGraficoPizzaHtml('Resultado do Apontamento Atual', [
        { label: 'Produção', value: this.formulario.QUANT_PRODUZIDA || 0, formatted: this.formatarNumero(this.formulario.QUANT_PRODUZIDA || 0), color: '#2eb85c' },
        { label: 'Sobra', value: this.formulario.QUANT_SOBRA || 0, formatted: this.formatarNumero(this.formulario.QUANT_SOBRA || 0), color: '#f9b115' },
        { label: 'Perda', value: this.formulario.QUANT_PERDA || 0, formatted: this.formatarNumero(this.formulario.QUANT_PERDA || 0), color: '#e55353' }
      ], this.formatarNumero(this.formulario.QUANT_PRODUZIDA || 0))

      const graficoProcesso = this.montarGraficoPizzaHtml('Impacto no Processo', [
        { label: 'Já produzido', value: this.processo.QUANT_PRODUZIDA || 0, formatted: this.formatarNumero(this.processo.QUANT_PRODUZIDA || 0), color: '#0d6efd' },
        { label: 'Apontamento atual', value: this.formulario.QUANT_PRODUZIDA || 0, formatted: this.formatarNumero(this.formulario.QUANT_PRODUZIDA || 0), color: '#20c997' },
        { label: 'Saldo restante', value: resumo.saldoAposApontamentoPositivo, formatted: this.formatarNumero(resumo.saldoAposApontamentoPositivo), color: '#adb5bd' },
        { label: 'Excedente', value: resumo.excedenteApontamento, formatted: this.formatarNumero(resumo.excedenteApontamento), color: '#dc3545' }
      ], this.formatarNumero(this.processo.QUANT_PREVISTA || 0))

      const graficoLotes = this.montarGraficoPizzaHtml('Controle de Lotes', [
        { label: 'Itens com lote', value: resumo.itensComLote, formatted: this.formatarInteiro(resumo.itensComLote), color: '#339af0' },
        { label: 'Rateio pendente', value: resumo.itensLotePendentes, formatted: this.formatarInteiro(resumo.itensLotePendentes), color: '#e55353' },
        { label: 'Itens sem lote', value: resumo.itensSemLote, formatted: this.formatarInteiro(resumo.itensSemLote), color: '#adb5bd' }
      ], this.formatarInteiro(resumo.lotesUtilizados || 0))

      const linhasItens = itensMovimento.length
        ? itensMovimento.map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(item.PRODUTO_CODIGONOME || '')}</td>
            <td>${escapeHtml(item.CENTRO_ESTOQUE_CODIGONOME || '')}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(this.getSaldoQuantidadeItem(item)))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_PREVISTA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_APONTADA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_CONSUMIDA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_SOBRA_ATUAL || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_PERDA_ATUAL || 0))}</td>
            <td style="text-align:center;">${escapeHtml(String(item.OP_CONTROLA_LOTE || '0') === '1' ? (this.possuiLotesValidos(item) ? 'OK' : 'Pendente') : 'N/A')}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarMoeda(this.calcularCustoMovimentoItem(item)))}</td>
          </tr>
        `).join('')
        : '<tr><td colspan="11" style="text-align:center; color:#6c757d;">Nenhum item movimentado no apontamento atual.</td></tr>'

      const blocosLotes = itensMovimento
        .filter(item => (item.LOTES || []).length)
        .map(item => {
          const linhasLotes = item.LOTES.map((lote, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(lote.LOTE || '')}</td>
              <td style="text-align:right;">${escapeHtml(this.formatarNumero(lote.QUANT_CONSUMIDA || 0))}</td>
              <td style="text-align:right;">${escapeHtml(this.formatarMoeda(Number(lote.QUANT_CONSUMIDA || 0) * Number(lote.CUSTO_UNITARIO || lote.VL_CUSTO_UNITARIO || 0)))}</td>
            </tr>
          `).join('')

          return `
            <div class="lot-card">
              <div class="lot-card-title">${escapeHtml(item.PRODUTO_CODIGONOME || '')} - ${escapeHtml(item.CENTRO_ESTOQUE_CODIGONOME || '')}</div>
              <table class="grid nested-grid">
                <thead>
                  <tr>
                    <th style="width:36px;">#</th>
                    <th>Lote</th>
                    <th style="width:110px; text-align:right;">Quantidade</th>
                    <th style="width:120px; text-align:right;">Custo Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${linhasLotes}
                </tbody>
              </table>
            </div>
          `
        }).join('')

      const linhasHistorico = historicoRecente.length
        ? historicoRecente.map(apontamento => `
          <tr>
            <td>${escapeHtml(apontamento.ID || '')}</td>
            <td>${escapeHtml(this.formatarDataHora(apontamento.DATA_APONTAMENTO || ''))}</td>
            <td>${escapeHtml(apontamento.USUARIO_CADASTRO || '')}</td>
            <td style="text-align:center;">${escapeHtml(apontamento.OP_STATUS_LABEL || '')}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(apontamento.QUANT_PRODUZIDA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(apontamento.QUANT_SOBRA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(apontamento.QUANT_PERDA || 0))}</td>
          </tr>
        `).join('')
        : '<tr><td colspan="7" style="text-align:center; color:#6c757d;">Nenhum apontamento anterior registrado.</td></tr>'

      return `<!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>Espelho do Apontamento Parcial</title>
        <style>
          body { font-family: Arial, Helvetica, sans-serif; margin: 16px; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .report-header { border: 1px solid #2f2f2f; }
          .report-header-top, .report-header-sub { display: flex; justify-content: space-between; padding: 4px 8px; font-size: 11px; border-bottom: 1px solid #2f2f2f; }
          .report-header-title { padding: 4px 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
          .section { margin-top: 10px; page-break-inside: avoid; }
          .section-title { font-size: 11px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; }
          .grid, .info-table { width: 100%; border-collapse: collapse; }
          .grid th, .grid td, .info-table td { border: 1px solid #2f2f2f; padding: 4px 6px; font-size: 11px; vertical-align: top; }
          .grid th { background: #f2f2f2; text-align: left; }
          .info-table td { width: 50%; }
          .kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin-top: 10px; }
          .kpi-card { border: 1px solid #2f2f2f; padding: 8px; }
          .kpi-card small { display:block; font-size:10px; text-transform:uppercase; color:#666; margin-bottom:4px; }
          .kpi-card strong { font-size:16px; }
          .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 10px; }
          .pie-card { border: 1px solid #2f2f2f; padding: 8px; min-height: 180px; }
          .pie-title { font-size: 10px; font-weight: 700; text-transform: uppercase; margin-bottom: 8px; text-align: center; }
          .pie-row { display: flex; flex-direction: column; align-items: center; gap: 10px; }
          .pie-chart { width: 100px; height: 100px; border-radius: 50%; position: relative; flex-shrink: 0; }
          .pie-center { position: absolute; inset: 20px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 12px; font-weight: 700; }
          .pie-legend { width: 100%; }
          .pie-legend-item { display: flex; justify-content: space-between; align-items: center; font-size: 9px; margin-bottom: 4px; border-bottom: 1px dashed #eee; padding-bottom: 2px; }
          .pie-legend-label { display: flex; align-items: center; gap: 4px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 120px; }
          .pie-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
          .lot-card { margin-top: 8px; }
          .lot-card-title { font-size: 11px; font-weight: 700; margin-bottom: 4px; }
          .nested-grid th { background: #fafafa; }
          .signature-area { margin-top: 40px; }
          .signature-line { border-top: 1px solid #2f2f2f; width: 320px; padding-top: 6px; font-size: 11px; text-align: center; }
          .report-footer { position: fixed; left: 16px; right: 16px; bottom: 8px; display: flex; justify-content: space-between; font-size: 10px; border-top: 1px solid #2f2f2f; padding-top: 4px; background: #fff; }
          @media print { @page { size: A4 portrait; margin: 10mm; } body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        .sobra-produto-wrapper { transition: background-color 0.3s ease, border-color 0.3s ease; }
.bg-warning-soft { background-color: rgba(249, 177, 21, 0.08) !important; }
</style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-header-top">
            <div><strong>${escapeHtml(empresa.FANTASIA || empresa.RAZAO_RELATORIO || empresa.RAZAO || 'CECWEB')}</strong></div>
            <div><strong>Usuário:</strong> ${escapeHtml(nomeUsuario)}</div>
          </div>
          <div class="report-header-title">Espelho do Apontamento Parcial - Produção por Fórmula</div>
          <div class="report-header-sub">
            <div>${escapeHtml(empresa.NOME_EMPRESA || '')}</div>
            <div><strong>Emitido em:</strong> ${escapeHtml(formatarDataHoraRelatorio())}</div>
          </div>
          <div class="report-header-sub" style="border-bottom:0;">
            <div><strong>Processo:</strong> ${escapeHtml(this.processo.ID || '')} | <strong>Lote final:</strong> ${escapeHtml(this.processo.LOTE || '')}</div>
            <div><strong>Situação:</strong> ${escapeHtml(this.getLabelSituacao(this.processo.SITUACAO))}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Dados do processo e do apontamento</div>
          <table class="info-table">
            <tr>
              <td><strong>Fórmula:</strong> ${escapeHtml(this.processo.FORMULA_DESCRICAO || '')}</td>
              <td><strong>Produto Final:</strong> ${escapeHtml(this.processo.PRODUTO_CODIGONOME || '')}</td>
            </tr>
            <tr>
              <td><strong>Centro de Produção:</strong> ${escapeHtml(this.processo.CENTRO_PRODUCAO_CODIGONOME || this.processo.CENTRO_PRODUCAO_ID || '')}</td>
              <td><strong>Produção Prevista:</strong> ${escapeHtml(this.formatarNumero(this.processo.QUANT_PREVISTA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Produção Acumulada Antes:</strong> ${escapeHtml(this.formatarNumero(this.processo.QUANT_PRODUZIDA || 0))}</td>
              <td><strong>Produção deste Apontamento:</strong> ${escapeHtml(this.formatarNumero(this.formulario.QUANT_PRODUZIDA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Sobra deste Apontamento:</strong> ${escapeHtml(this.formatarNumero(this.formulario.QUANT_SOBRA || 0))}</td>
              <td><strong>Perda deste Apontamento:</strong> ${escapeHtml(this.formatarNumero(this.formulario.QUANT_PERDA || 0))}</td>
            </tr>
            ${this.formulario.SOBRA_TRANSFORMAR ? `
            <tr>
              <td><strong>Produto Gerado pela Sobra:</strong> ${escapeHtml(this.formulario.SOBRA_PRODUTO_CODIGONOME || '---')}</td>
              <td><strong>Lote Gerado pela Sobra:</strong> ${escapeHtml(this.formulario.SOBRA_LOTE || '---')}</td>
            </tr>
            ` : ''}
            <tr>
              <td><strong>Peças Produzidas:</strong> ${escapeHtml(this.formatarInteiro(this.formulario.PECAS_PRODUZIDA || 0))}</td>
              <td><strong>Total de Itens com Movimento:</strong> ${escapeHtml(this.formatarInteiro(resumo.itensMovimento || 0))}</td>
            </tr>
            <tr>
              <td colspan="2"><strong>Observação:</strong> ${escapeHtml(this.formulario.OBSERVACAO || '')}</td>
            </tr>
          </table>

          <div class="kpi-grid">
            <div class="kpi-card"><small>Consumo total</small><strong>${escapeHtml(this.formatarNumero(this.resumoApontamento.consumo || 0))}</strong></div>
            <div class="kpi-card"><small>Custo estimado</small><strong>${escapeHtml(this.formatarMoeda(resumo.custoMovimento || 0))}</strong></div>
            <div class="kpi-card"><small>Lotes utilizados</small><strong>${escapeHtml(this.formatarInteiro(resumo.lotesUtilizados || 0))}</strong></div>
            <div class="kpi-card"><small>Saldo após lançamento</small><strong>${escapeHtml(this.formatarNumero(resumo.saldoAposApontamento || 0))}</strong></div>
            <div class="kpi-card"><small>Custos operacionais reais</small><strong>${escapeHtml(this.formatarMoeda((this.custosReais || []).reduce((acc, c) => acc + (Number(c.VALOR) || 0), 0)))}</strong></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Indicadores visuais</div>
          <div class="charts-grid">
            ${graficoResultadoApontamento}
            ${graficoProcesso}
            ${graficoLotes}
          </div>
        </div>

        <div class="section">
          <div class="section-title">Itens movimentados neste apontamento</div>
          <table class="grid">
            <thead>
              <tr>
                <th style="width:36px;">#</th>
                <th style="width:190px;">Matéria-Prima</th>
                <th style="width:170px;">Centro</th>
                <th style="width:90px; text-align:right;">Saldo</th>
                <th style="width:90px; text-align:right;">Previsto</th>
                <th style="width:90px; text-align:right;">Acumulado</th>
                <th style="width:90px; text-align:right;">Atual</th>
                <th style="width:90px; text-align:right;">Sobra</th>
                <th style="width:90px; text-align:right;">Perda</th>
                <th style="width:75px; text-align:center;">Lotes</th>
                <th style="width:100px; text-align:right;">Custo</th>
              </tr>
            </thead>
            <tbody>
              ${linhasItens}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Rateio por lote</div>
          ${blocosLotes || '<div style="border:1px solid #2f2f2f; padding:8px; font-size:11px; color:#6c757d;">Nenhum rateio de lote informado no apontamento atual.</div>'}
        </div>

        <div class="section">
          <div class="section-title">Histórico resumido do processo</div>
          <table class="info-table">
            <tr>
              <td><strong>Apontamentos ativos:</strong> ${escapeHtml(this.formatarInteiro(this.apontamentosAtivos.length))}</td>
              <td><strong>Apontamentos cancelados:</strong> ${escapeHtml(this.formatarInteiro(this.apontamentosCancelados.length))}</td>
            </tr>
          </table>
          <table class="grid" style="margin-top:8px;">
            <thead>
              <tr>
                <th style="width:50px;">ID</th>
                <th style="width:150px;">Data</th>
                <th style="width:140px;">Usuário</th>
                <th style="width:90px; text-align:center;">Status</th>
                <th style="width:100px; text-align:right;">Produção</th>
                <th style="width:90px; text-align:right;">Sobra</th>
                <th style="width:90px; text-align:right;">Perda</th>
              </tr>
            </thead>
            <tbody>
              ${linhasHistorico}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Custos Operacionais Reais do Processo</div>
          ${(() => {
            const custos = this.custosReais || []
            if (!custos.length) {
              return '<div style="border:1px solid #2f2f2f; padding:8px; font-size:11px; color:#6c757d;">Nenhum custo operacional real registrado para este processo.</div>'
            }
            const totalCustos = custos.reduce((acc, c) => acc + (Number(c.VALOR) || 0), 0)
            const linhasCustos = custos.map((c, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${escapeHtml(this.formatarDataHora(c.DATA_LANCAMENTO || ''))}</td>
                <td>${escapeHtml(c.NATUREZA_DESCRICAO || '')}</td>
                <td style="text-align:center;">${c.PRODUTO_ID ? 'Produto' : 'Manual'}</td>
                <td>${escapeHtml(c.PRODUTO_CODIGONOME || c.PRODUTO_DESCRICAO || '-')}</td>
                <td style="text-align:right;">${c.QUANTIDADE_EXECUTADA ? escapeHtml(this.formatarNumero(c.QUANTIDADE_EXECUTADA)) : '-'}</td>
                <td style="text-align:right;">${c.VALOR_UNITARIO_APLICADO ? escapeHtml(this.formatarMoeda(c.VALOR_UNITARIO_APLICADO)) : '-'}</td>
                <td style="text-align:right; font-weight:700;">${escapeHtml(this.formatarMoeda(c.VALOR || 0))}</td>
              </tr>
            `).join('')
            return `
              <table class="grid">
                <thead>
                  <tr>
                    <th style="width:30px;">#</th>
                    <th style="width:130px;">Data</th>
                    <th>Natureza</th>
                    <th style="width:70px; text-align:center;">Tipo</th>
                    <th>Produto / Insumo</th>
                    <th style="width:90px; text-align:right;">Qtd.</th>
                    <th style="width:110px; text-align:right;">Vlr. Unit.</th>
                    <th style="width:120px; text-align:right;">Valor Total</th>
                  </tr>
                </thead>
                <tbody>${linhasCustos}</tbody>
                <tfoot>
                  <tr style="background:#f2f2f2; font-weight:700;">
                    <td colspan="7" style="text-align:right;">TOTAL CUSTOS OPERACIONAIS REAIS:</td>
                    <td style="text-align:right; font-size:13px;">${escapeHtml(this.formatarMoeda(totalCustos))}</td>
                  </tr>
                </tfoot>
              </table>
            `
          })()}
        </div>

        <div class="signature-area">
          <div class="signature-line">Assinatura do usuário responsável</div>
        </div>

        <div class="report-footer">
          <div>Impresso em: ${escapeHtml(formatarDataHoraRelatorio())}</div>
          <div>Itens movimentados: ${escapeHtml(this.formatarInteiro(resumo.itensMovimento || 0))}</div>
          <div>Usuário: ${escapeHtml(nomeUsuario)}</div>
        </div>
      </body>
      </html>`
    },
    montarHtmlEspelhoApontamentoHistorico(empresa, apontamento, itensDetalhados) {
      const nomeUsuario = window.localStorage.getItem('nomeusuario') || window.localStorage.getItem('login') || 'sistema'
      const itensMovimento = this.getItensComMovimentoApontamento(itensDetalhados)
      const resumo = this.getResumoEspelhoApontamentoHistorico(apontamento, itensDetalhados)
      const historicoRecente = (this.apontamentos || []).slice(0, 5)

      const graficoResultadoApontamento = this.montarGraficoPizzaHtml('Resultado do Apontamento', [
        { label: 'Produção', value: apontamento.QUANT_PRODUZIDA || 0, formatted: this.formatarNumero(apontamento.QUANT_PRODUZIDA || 0), color: '#2eb85c' },
        { label: 'Sobra', value: apontamento.QUANT_SOBRA || 0, formatted: this.formatarNumero(apontamento.QUANT_SOBRA || 0), color: '#f9b115' },
        { label: 'Perda', value: apontamento.QUANT_PERDA || 0, formatted: this.formatarNumero(apontamento.QUANT_PERDA || 0), color: '#e55353' }
      ], this.formatarNumero(apontamento.QUANT_PRODUZIDA || 0))

      const graficoProcesso = this.montarGraficoPizzaHtml('Impacto no Processo', [
        { label: 'Produzido antes', value: resumo.producaoAntes || 0, formatted: this.formatarNumero(resumo.producaoAntes || 0), color: '#0d6efd' },
        { label: 'Neste apontamento', value: apontamento.QUANT_PRODUZIDA || 0, formatted: this.formatarNumero(apontamento.QUANT_PRODUZIDA || 0), color: '#20c997' },
        { label: 'Saldo restante', value: resumo.saldoAposApontamentoPositivo, formatted: this.formatarNumero(resumo.saldoAposApontamentoPositivo), color: '#adb5bd' },
        { label: 'Excedente', value: resumo.excedenteApontamento, formatted: this.formatarNumero(resumo.excedenteApontamento), color: '#dc3545' }
      ], this.formatarNumero(this.processo.QUANT_PREVISTA || 0))

      const graficoLotes = this.montarGraficoPizzaHtml('Controle de Lotes', [
        { label: 'Itens com lote', value: resumo.itensComLote, formatted: this.formatarInteiro(resumo.itensComLote), color: '#339af0' },
        { label: 'Rateio pendente', value: resumo.itensLotePendentes, formatted: this.formatarInteiro(resumo.itensLotePendentes), color: '#e55353' },
        { label: 'Itens sem lote', value: resumo.itensSemLote, formatted: this.formatarInteiro(resumo.itensSemLote), color: '#adb5bd' }
      ], this.formatarInteiro(resumo.lotesUtilizados || 0))

      const linhasItens = itensMovimento.length
        ? itensMovimento.map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(item.PRODUTO_CODIGONOME || '')}</td>
            <td>${escapeHtml(item.CENTRO_ESTOQUE_CODIGONOME || '')}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_CONSUMIDA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_SOBRA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_PERDA || 0))}</td>
            <td style="text-align:center;">${escapeHtml(String(item.OP_CONTROLA_LOTE || '0') === '1' ? ((item.LOTES || []).length ? 'OK' : 'Sem rateio') : 'N/A')}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarMoeda(Number(item.CUSTO_TOTAL || this.calcularCustoMovimentoItem(item))))}</td>
          </tr>
        `).join('')
        : '<tr><td colspan="8" style="text-align:center; color:#6c757d;">Nenhum item movimentado neste apontamento.</td></tr>'

      const blocosLotes = itensMovimento
        .filter(item => (item.LOTES || []).length)
        .map(item => {
          const linhasLotes = item.LOTES.map((lote, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(lote.LOTE || '')}</td>
              <td style="text-align:right;">${escapeHtml(this.formatarNumero(lote.QUANT_CONSUMIDA || 0))}</td>
              <td style="text-align:right;">${escapeHtml(this.formatarMoeda(Number(lote.CUSTO_TOTAL || ((lote.QUANT_CONSUMIDA || 0) * (lote.CUSTO_UNITARIO || lote.VL_CUSTO_UNITARIO || 0)))))}</td>
            </tr>
          `).join('')

          return `
            <div class="lot-card">
              <div class="lot-card-title">${escapeHtml(item.PRODUTO_CODIGONOME || '')} - ${escapeHtml(item.CENTRO_ESTOQUE_CODIGONOME || '')}</div>
              <table class="grid nested-grid">
                <thead>
                  <tr>
                    <th style="width:36px;">#</th>
                    <th>Lote</th>
                    <th style="width:110px; text-align:right;">Quantidade</th>
                    <th style="width:120px; text-align:right;">Custo Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${linhasLotes}
                </tbody>
              </table>
            </div>
          `
        }).join('')

      const linhasHistorico = historicoRecente.length
        ? historicoRecente.map(item => `
          <tr>
            <td>${escapeHtml(item.ID || '')}</td>
            <td>${escapeHtml(this.formatarDataHora(item.DATA_APONTAMENTO || ''))}</td>
            <td>${escapeHtml(item.USUARIO_CADASTRO || '')}</td>
            <td style="text-align:center;">${escapeHtml(item.OP_STATUS_LABEL || '')}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_PRODUZIDA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_SOBRA || 0))}</td>
            <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_PERDA || 0))}</td>
          </tr>
        `).join('')
        : '<tr><td colspan="7" style="text-align:center; color:#6c757d;">Nenhum apontamento anterior registrado.</td></tr>'

      return `<!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>Espelho do Apontamento Parcial</title>
        <style>
          body { font-family: Arial, Helvetica, sans-serif; margin: 16px; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .report-header { border: 1px solid #2f2f2f; }
          .report-header-top, .report-header-sub { display: flex; justify-content: space-between; padding: 4px 8px; font-size: 11px; border-bottom: 1px solid #2f2f2f; }
          .report-header-title { padding: 4px 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
          .section { margin-top: 10px; page-break-inside: avoid; }
          .section-title { font-size: 11px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; }
          .grid, .info-table { width: 100%; border-collapse: collapse; }
          .grid th, .grid td, .info-table td { border: 1px solid #2f2f2f; padding: 4px 6px; font-size: 11px; vertical-align: top; }
          .grid th { background: #f2f2f2; text-align: left; }
          .info-table td { width: 50%; }
          .kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin-top: 10px; }
          .kpi-card { border: 1px solid #2f2f2f; padding: 8px; }
          .kpi-card small { display:block; font-size:10px; text-transform:uppercase; color:#666; margin-bottom:4px; }
          .kpi-card strong { font-size:16px; }
          .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 10px; }
          .pie-card { border: 1px solid #2f2f2f; padding: 8px; min-height: 180px; }
          .pie-title { font-size: 10px; font-weight: 700; text-transform: uppercase; margin-bottom: 8px; text-align: center; }
          .pie-row { display: flex; flex-direction: column; align-items: center; gap: 10px; }
          .pie-chart { width: 100px; height: 100px; border-radius: 50%; position: relative; flex-shrink: 0; }
          .pie-center { position: absolute; inset: 20px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 12px; font-weight: 700; }
          .pie-legend { width: 100%; }
          .pie-legend-item { display: flex; justify-content: space-between; align-items: center; font-size: 9px; margin-bottom: 4px; border-bottom: 1px dashed #eee; padding-bottom: 2px; }
          .pie-legend-label { display: flex; align-items: center; gap: 4px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 120px; }
          .pie-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
          .lot-card { margin-top: 8px; }
          .lot-card-title { font-size: 11px; font-weight: 700; margin-bottom: 4px; }
          .nested-grid th { background: #fafafa; }
          .signature-area { margin-top: 40px; }
          .signature-line { border-top: 1px solid #2f2f2f; width: 320px; padding-top: 6px; font-size: 11px; text-align: center; }
          .report-footer { position: fixed; left: 16px; right: 16px; bottom: 8px; display: flex; justify-content: space-between; font-size: 10px; border-top: 1px solid #2f2f2f; padding-top: 4px; background: #fff; }
          @media print { @page { size: A4 portrait; margin: 10mm; } body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-header-top">
            <div><strong>${escapeHtml(empresa.FANTASIA || empresa.RAZAO_RELATORIO || empresa.RAZAO || 'CECWEB')}</strong></div>
            <div><strong>Usuário:</strong> ${escapeHtml(nomeUsuario)}</div>
          </div>
          <div class="report-header-title">Espelho do Apontamento Parcial - Produção por Fórmula</div>
          <div class="report-header-sub">
            <div>${escapeHtml(empresa.NOME_EMPRESA || '')}</div>
            <div><strong>Emitido em:</strong> ${escapeHtml(formatarDataHoraRelatorio())}</div>
          </div>
          <div class="report-header-sub" style="border-bottom:0;">
            <div><strong>Processo:</strong> ${escapeHtml(this.processo.ID || '')} | <strong>Lote final:</strong> ${escapeHtml(this.processo.LOTE || '')}</div>
            <div><strong>Status do apontamento:</strong> ${escapeHtml(apontamento.OP_STATUS_LABEL || (String(apontamento.OP_STATUS || '1') === '1' ? 'Ativo' : 'Cancelado'))}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Dados do processo e do apontamento</div>
          <table class="info-table">
            <tr>
              <td><strong>Data do apontamento:</strong> ${escapeHtml(this.formatarDataHora(apontamento.DATA_APONTAMENTO || ''))}</td>
              <td><strong>Usuário responsável:</strong> ${escapeHtml(apontamento.USUARIO_CADASTRO || '')}</td>
            </tr>
            <tr>
              <td><strong>Fórmula:</strong> ${escapeHtml(this.processo.FORMULA_DESCRICAO || '')}</td>
              <td><strong>Produto Final:</strong> ${escapeHtml(this.processo.PRODUTO_CODIGONOME || '')}</td>
            </tr>
            <tr>
              <td><strong>Centro de Produção:</strong> ${escapeHtml(this.processo.CENTRO_PRODUCAO_CODIGONOME || this.processo.CENTRO_PRODUCAO_ID || '')}</td>
              <td><strong>Produção Prevista:</strong> ${escapeHtml(this.formatarNumero(this.processo.QUANT_PREVISTA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Produção acumulada antes:</strong> ${escapeHtml(this.formatarNumero(resumo.producaoAntes || 0))}</td>
              <td><strong>Produção deste apontamento:</strong> ${escapeHtml(this.formatarNumero(apontamento.QUANT_PRODUZIDA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Produção acumulada após:</strong> ${escapeHtml(this.formatarNumero(resumo.producaoDepois || 0))}</td>
              <td><strong>Saldo restante após apontamento:</strong> ${escapeHtml(this.formatarNumero(resumo.saldoAposApontamento || 0))}</td>
            </tr>
            <tr>
              <td><strong>Sobra do apontamento:</strong> ${escapeHtml(this.formatarNumero(apontamento.QUANT_SOBRA || 0))}</td>
              <td><strong>Perda do apontamento:</strong> ${escapeHtml(this.formatarNumero(apontamento.QUANT_PERDA || 0))}</td>
            </tr>
            ${apontamento.SOBRA_PRODUTO_ID ? `
            <tr>
              <td><strong>Produto Gerado pela Sobra:</strong> ${escapeHtml(apontamento.SOBRA_PRODUTO_CODIGONOME || '---')}</td>
              <td><strong>Lote Gerado pela Sobra:</strong> ${escapeHtml(apontamento.SOBRA_LOTE || '---')}</td>
            </tr>
            ` : ''}
            <tr>
              <td><strong>Peças Produzidas:</strong> ${escapeHtml(this.formatarInteiro(apontamento.PECAS_PRODUZIDA || 0))}</td>
              <td><strong>Total de Itens com Movimento:</strong> ${escapeHtml(this.formatarInteiro(resumo.itensMovimento || 0))}</td>
            </tr>
            <tr>
              <td colspan="2"><strong>Observação:</strong> ${escapeHtml(apontamento.OBSERVACAO || '')}</td>
            </tr>
          </table>

          <div class="kpi-grid">
            <div class="kpi-card"><small>Consumo total</small><strong>${escapeHtml(this.formatarNumero(itensMovimento.reduce((total, item) => total + Number(item.QUANT_CONSUMIDA || 0), 0)))}</strong></div>
            <div class="kpi-card"><small>Custo apurado</small><strong>${escapeHtml(this.formatarMoeda(resumo.custoMovimento || 0))}</strong></div>
            <div class="kpi-card"><small>Lotes utilizados</small><strong>${escapeHtml(this.formatarInteiro(resumo.lotesUtilizados || 0))}</strong></div>
            <div class="kpi-card"><small>Itens movimentados</small><strong>${escapeHtml(this.formatarInteiro(resumo.itensMovimento || 0))}</strong></div>
            <div class="kpi-card"><small>Custos operacionais reais</small><strong>${escapeHtml(this.formatarMoeda((this.custosReais || []).reduce((acc, c) => acc + (Number(c.VALOR) || 0), 0)))}</strong></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Indicadores visuais</div>
          <div class="charts-grid">
            ${graficoResultadoApontamento}
            ${graficoProcesso}
            ${graficoLotes}
          </div>
        </div>

        <div class="section">
          <div class="section-title">Itens movimentados no apontamento</div>
          <table class="grid">
            <thead>
              <tr>
                <th style="width:36px;">#</th>
                <th>Matéria-Prima</th>
                <th>Centro</th>
                <th style="width:90px; text-align:right;">Consumo</th>
                <th style="width:90px; text-align:right;">Sobra</th>
                <th style="width:90px; text-align:right;">Perda</th>
                <th style="width:100px; text-align:right;">Custo</th>
              </tr>
            </thead>
            <tbody>
              ${linhasItens}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Rateio por lote</div>
          ${blocosLotes || '<div style="border:1px solid #2f2f2f; padding:8px; font-size:11px; color:#6c757d;">Nenhum rateio de lote informado neste apontamento.</div>'}
        </div>

        <div class="section">
          <div class="section-title">Histórico resumido do processo</div>
          <table class="info-table">
            <tr>
              <td><strong>Apontamentos ativos:</strong> ${escapeHtml(this.formatarInteiro(this.apontamentosAtivos.length))}</td>
              <td><strong>Apontamentos cancelados:</strong> ${escapeHtml(this.formatarInteiro(this.apontamentosCancelados.length))}</td>
            </tr>
          </table>
          <table class="grid" style="margin-top:8px;">
            <thead>
              <tr>
                <th style="width:50px;">ID</th>
                <th style="width:150px;">Data</th>
                <th style="width:140px;">Usuário</th>
                <th style="width:90px; text-align:center;">Status</th>
                <th style="width:100px; text-align:right;">Produção</th>
                <th style="width:90px; text-align:right;">Sobra</th>
                <th style="width:90px; text-align:right;">Perda</th>
              </tr>
            </thead>
            <tbody>
              ${linhasHistorico}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Custos Operacionais Reais do Processo</div>
          ${(() => {
            const custos = this.custosReais || []
            if (!custos.length) {
              return '<div style="border:1px solid #2f2f2f; padding:8px; font-size:11px; color:#6c757d;">Nenhum custo operacional real registrado para este processo.</div>'
            }
            const totalCustos = custos.reduce((acc, c) => acc + (Number(c.VALOR) || 0), 0)
            const linhasCustos = custos.map((c, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${escapeHtml(this.formatarDataHora(c.DATA_LANCAMENTO || ''))}</td>
                <td>${escapeHtml(c.NATUREZA_DESCRICAO || '')}</td>
                <td style="text-align:center;">${c.PRODUTO_ID ? 'Produto' : 'Manual'}</td>
                <td>${escapeHtml(c.PRODUTO_CODIGONOME || c.PRODUTO_DESCRICAO || '-')}</td>
                <td style="text-align:right;">${c.QUANTIDADE_EXECUTADA ? escapeHtml(this.formatarNumero(c.QUANTIDADE_EXECUTADA)) : '-'}</td>
                <td style="text-align:right;">${c.VALOR_UNITARIO_APLICADO ? escapeHtml(this.formatarMoeda(c.VALOR_UNITARIO_APLICADO)) : '-'}</td>
                <td style="text-align:right; font-weight:700;">${escapeHtml(this.formatarMoeda(c.VALOR || 0))}</td>
              </tr>
            `).join('')
            return `
              <table class="grid">
                <thead>
                  <tr>
                    <th style="width:30px;">#</th>
                    <th style="width:130px;">Data</th>
                    <th>Natureza</th>
                    <th style="width:70px; text-align:center;">Tipo</th>
                    <th>Produto / Insumo</th>
                    <th style="width:90px; text-align:right;">Qtd.</th>
                    <th style="width:110px; text-align:right;">Vlr. Unit.</th>
                    <th style="width:120px; text-align:right;">Valor Total</th>
                  </tr>
                </thead>
                <tbody>${linhasCustos}</tbody>
                <tfoot>
                  <tr style="background:#f2f2f2; font-weight:700;">
                    <td colspan="7" style="text-align:right;">TOTAL CUSTOS OPERACIONAIS REAIS:</td>
                    <td style="text-align:right; font-size:13px;">${escapeHtml(this.formatarMoeda(totalCustos))}</td>
                  </tr>
                </tfoot>
              </table>
            `
          })()}
        </div>

        <div class="signature-area">
          <div class="signature-line">Assinatura do usuário responsável</div>
        </div>

        <div class="report-footer">
          <div>Impresso em: ${escapeHtml(formatarDataHoraRelatorio())}</div>
          <div>Apontamento: ${escapeHtml(apontamento.ID || '')}</div>
          <div>Usuário: ${escapeHtml(nomeUsuario)}</div>
        </div>
      </body>
      </html>`
    },
    async imprimirEspelhoApontamento() {
      const janela = window.open('', '_blank')
      if (!janela) {
        this.$bvToast.toast('O navegador bloqueou a abertura da nova aba do espelho.', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      try {
        const empresa = await this.obterEmpresaPreferencial()
        const html = this.montarHtmlEspelhoApontamento(empresa)
        janela.document.open('text/html', 'replace')
        janela.document.write(html)
        janela.document.close()
        janela.focus()
      } catch (err) {
        janela.close()
        this.$bvToast.toast(err?.response?.data || err?.message || 'Erro ao gerar espelho do apontamento', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },
    async carregarItensDetalhadosApontamento(apontamentoId) {
      const { data } = await axios.get(`/api/protected/producao-formula-apontamento-item/listar/${apontamentoId}`)
      const itens = data || []

      return Promise.all(itens.map(async item => {
        if (Number(item.TOTAL_LOTES || 0) <= 0) {
          return { ...item, LOTES: [], _showDetails: false }
        }

        const responseLotes = await axios.get(`/api/protected/producao-formula-apontamento-item/lotes-apontados/${item.ID}`)
        const lotes = responseLotes.data || []
        return {
          ...item,
          LOTES: lotes,
          _showDetails: lotes.length > 0
        }
      }))
    },
    async imprimirEspelhoApontamentoHistorico(apontamento) {
      const janela = window.open('', '_blank')
      if (!janela) {
        this.$bvToast.toast('O navegador bloqueou a abertura da nova aba do espelho.', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      try {
        const [empresa, itensDetalhados] = await Promise.all([
          this.obterEmpresaPreferencial(),
          this.carregarItensDetalhadosApontamento(apontamento.ID)
        ])
        const html = this.montarHtmlEspelhoApontamentoHistorico(empresa, apontamento, itensDetalhados)
        janela.document.open('text/html', 'replace')
        janela.document.write(html)
        janela.document.close()
        janela.focus()
      } catch (err) {
        janela.close()
        this.$bvToast.toast(err?.response?.data || err?.message || 'Erro ao gerar espelho do apontamento', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },
    getLabelSituacao(situacao) {
      if (String(situacao || 'L') === 'F') return 'Finalizado'
      if (String(situacao || 'L') === 'C') return 'Cancelado'
      return 'Lançamento'
    },
    getSaldoQuantidadeItem(item) {
      return Number(item.QUANT_DISPONIVEL !== undefined ? item.QUANT_DISPONIVEL : item.ESTOQUE_QUANT || 0)
    },
    getSaldoPecasItem(item) {
      return Number(item.PECAS_DISPONIVEL !== undefined ? item.PECAS_DISPONIVEL : item.ESTOQUE_PECAS || 0)
    },
    formatarDataHora(valor) {
      if (!valor) return ''
      return new Date(valor).toLocaleString('pt-BR')
    },
    getItemById(itemId) {
      return this.itens.find(item => Number(item.ID) === Number(itemId)) || null
    },
    possuiMovimentoItem(item) {
      return Number(item.QUANT_CONSUMIDA || 0) > 0 || Number(item.PECAS_CONSUMIDAS || 0) > 0 || Number(item.QUANT_SOBRA_ATUAL || 0) > 0 || Number(item.PECAS_SOBRA_ATUAL || 0) > 0 || Number(item.QUANT_PERDA_ATUAL || 0) > 0 || Number(item.PECAS_PERDA_ATUAL || 0) > 0
    },
    possuiLotesValidos(item) {
      if (String(item.OP_CONTROLA_LOTE || '0') !== '1') return true
      if (Number(item.QUANT_CONSUMIDA || 0) <= 0 && Number(item.PECAS_CONSUMIDAS || 0) <= 0) return true

      const quantidade = item.LOTES.reduce((total, lote) => total + Number(lote.QUANT_CONSUMIDA || 0), 0)
      const pecas = item.LOTES.reduce((total, lote) => total + Number(lote.PECAS_CONSUMIDAS || 0), 0)
      if (Number(item.QUANT_CONSUMIDA || 0) > 0 && Math.abs(quantidade - Number(item.QUANT_CONSUMIDA || 0)) > 0.0005) return false
      if (Number(item.PECAS_CONSUMIDAS || 0) > 0 && pecas !== Number(item.PECAS_CONSUMIDAS || 0)) return false
      return item.LOTES.length > 0
    },
    getVariantLote(item) {
      if (!this.possuiMovimentoItem(item)) return 'secondary'
      return this.possuiLotesValidos(item) ? 'success' : 'danger'
    },
    getTextoAcaoLote(item) {
      if (!this.podeEditarApontamento) return 'Consulta disponível no histórico'
      if (!this.possuiMovimentoItem(item)) return 'Selecionar lotes'
      return this.possuiLotesValidos(item) ? 'Rateio concluído' : 'Ratear lotes'
    },
    getTextoStatusLote(item) {
      if (!this.possuiMovimentoItem(item)) return 'Sem consumo'
      if (this.possuiLotesValidos(item)) return `${item.LOTES.length} lote(s)`
      return 'Rateio pendente'
    },
    classeCampoConsumo(item, semMargem = false) {
      return [
        'form-control',
        'form-control-sm',
        'rounded-pill',
        'text-right',
        'shadow-xs',
        semMargem ? '' : 'mb-1',
        { 'is-invalid': this.submitAttempted && !this.temItensComMovimento }
      ]
    },
    classeLinhaItem(item) {
      if (!this.podeEditarApontamento) return ''
      if (!item) return ''
      if (String(item.OP_CONTROLA_LOTE || '0') === '1' && this.possuiMovimentoItem(item) && !this.possuiLotesValidos(item)) {
        return 'linha-rateio-pendente'
      }
      if (String(item.OP_CONTROLA_LOTE || '0') === '1') {
        return 'linha-controla-lote'
      }
      return ''
    },
    async carregarTela() {
      this.isBusy = true
      try {
        const processoId = this.$route.params.processoId
        const [processoResponse, itensResponse, apontamentosResponse, naturezasResponse] = await Promise.all([
          axios.get(`/api/protected/producao-formula-processo/getById/${processoId}`),
          axios.get(`/api/protected/producao-formula-processo-item/listar/${processoId}`),
          axios.get(`/api/protected/producao-formula-apontamento/listar/${processoId}`),
          axios.get('/api/protected/natureza-custo/getListaLocaliza')
        ])

        this.processo = processoResponse.data?.[0] || {}
        await this.carregarCustosReais()

        // Cálculo do saldo de peças/volumes restante (Peças Previstas - Peças já apontadas/QUANT_PRODUZIDA no DB)
        const pecasRestante = Math.max(0, Number(this.processo.PECAS_PREVISTA || 0) - Number(this.processo.QUANT_PRODUZIDA || 0))
        
        // Cálculo da produção (Quantidade) proporcional às peças restantes
        let producaoRestante = 0
        if (Number(this.processo.PECAS_PREVISTA || 0) > 0) {
          producaoRestante = (pecasRestante * Number(this.processo.QUANT_PREVISTA || 0)) / Number(this.processo.PECAS_PREVISTA)
        } else {
          producaoRestante = Math.max(0, Number(this.processo.QUANT_PREVISTA || 0) - Number(this.processo.PECAS_PRODUZIDA || 0))
        }

        this.itens = (itensResponse.data || []).map(item => {
          const nItem = novoItem(item)
          // Pré-preenche o consumo com o saldo previsto restante do item (consumo proporcional ao que falta produzir)
          nItem.QUANT_CONSUMIDA = Math.max(0, Number(item.QUANT_PREVISTA || 0) - Number(item.QUANT_APONTADA || 0))
          return nItem
        })

        this.apontamentos = apontamentosResponse.data || []
        this.formulario = novoFormulario(this.processo.ID)

        // Define os valores iniciais de produção
        this.formulario.PECAS_PRODUZIDA = pecasRestante
        this.formulario.QUANT_PRODUZIDA = producaoRestante

        this.submitAttempted = false
        this.opcoesNaturezas = (naturezasResponse.data || []).map(n => ({ value: n.ID, text: n.DESCRICAO }))
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao carregar o apontamento parcial', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
        this.voltar()
      } finally {
        this.isBusy = false
      }
    },
    recalcularProducao(item) {
      if (!item || !this.processo.QUANT_PREVISTA || !item.QUANT_PREVISTA) return

      const quantConsumida = Number(item.QUANT_CONSUMIDA || 0)
      const quantPrevistaItem = Number(item.QUANT_PREVISTA || 0)
      const quantPrevistaProcesso = Number(this.processo.QUANT_PREVISTA || 0)

      if (quantPrevistaItem > 0) {
        // Regra de Três:
        // Produção Prevista --- Consumo Previsto Item
        // Produção Atual    --- Consumo Atual Item
        // Produção Atual = (Consumo Atual * Produção Prevista) / Consumo Previsto Item
        const novaProducao = (quantConsumida * quantPrevistaProcesso) / quantPrevistaItem

        this.formulario.QUANT_PRODUZIDA = novaProducao
        this.formulario.PECAS_PRODUZIDA = novaProducao // Mapping rule do sistema: Peças (UI) vira Qtd (DB)
      }
    },
    recalcularQuantidadePorPecas() {
      const pecas = Number(this.formulario.PECAS_PRODUZIDA || 0)
      const pecasPrevista = Number(this.processo.PECAS_PREVISTA || 0)
      const quantPrevista = Number(this.processo.QUANT_PREVISTA || 0)

      if (pecasPrevista > 0) {
        this.formulario.QUANT_PRODUZIDA = (pecas * quantPrevista) / pecasPrevista
      } else {
        this.formulario.QUANT_PRODUZIDA = pecas
      }
    },
    validarFormulario() {
      this.submitAttempted = true
      if (!this.processo.ID) return 'Processo inválido para apontamento'
      if (Number(this.formulario.QUANT_PRODUZIDA || 0) <= 0 && Number(this.formulario.PECAS_PRODUZIDA || 0) <= 0) {
        return 'Informe a quantidade produzida ou as peças produzidas'
      }

      if (this.formulario.SOBRA_TRANSFORMAR) {
          if (Number(this.formulario.QUANT_SOBRA || 0) <= 0) {
            return 'Para transformar sobra em produto acabado, informe a quantidade de sobra'
          }
          if (!this.formulario.SOBRA_PRODUTO_ID) {
            return 'Selecione o produto acabado gerado pela sobra'
          }
          if (!String(this.formulario.SOBRA_LOTE || '').trim()) {
            return 'Informe o lote do produto gerado pela sobra'
          }
        }
        
        const itensComMovimento = this.itens.filter(item => this.possuiMovimentoItem(item))
      if (!itensComMovimento.length) {
        return 'Informe ao menos um consumo real no apontamento'
      }

      for (const item of itensComMovimento) {
        if (Number(item.QUANT_CONSUMIDA || 0) > this.getSaldoQuantidadeItem(item) + 0.0005) {
          return `Saldo insuficiente em estoque para o item ${item.PRODUTO_CODIGONOME}`
        }

        if (Number(item.PECAS_CONSUMIDAS || 0) > this.getSaldoPecasItem(item)) {
          return `Peças insuficientes em estoque para o item ${item.PRODUTO_CODIGONOME}`
        }

        if (String(item.OP_CONTROLA_LOTE || '0') === '1' && !this.possuiLotesValidos(item)) {
          return `Conclua o rateio dos lotes para o item ${item.PRODUTO_CODIGONOME}`
        }
      }

      return ''
    },
    montarPayload() {
      return {
        login: window.localStorage.getItem('login') || 'SISTEMA',
        registro: {
          PRD_FORM_PROC_ID: this.processo.ID,
          QUANT_PRODUZIDA: Number(this.formulario.PECAS_PRODUZIDA || 0),
          PECAS_PRODUZIDA: 0,
          QUANT_SOBRA: Number(this.formulario.QUANT_SOBRA || 0),
          PECAS_SOBRA: Number(this.formulario.PECAS_SOBRA || 0),
          QUANT_PERDA: Number(this.formulario.QUANT_PERDA || 0),
          PECAS_PERDA: Number(this.formulario.PECAS_PERDA || 0),
          OBSERVACAO: this.formulario.OBSERVACAO,
            SOBRA_TRANSFORMAR: !!this.formulario.SOBRA_TRANSFORMAR,
            SOBRA_PRODUTO_ID: this.formulario.SOBRA_TRANSFORMAR ? Number(this.formulario.SOBRA_PRODUTO_ID) : null,
            SOBRA_LOTE: this.formulario.SOBRA_TRANSFORMAR ? String(this.formulario.SOBRA_LOTE || '').trim().toUpperCase() : null
          },
        itens: this.itens
          .filter(item => this.possuiMovimentoItem(item))
          .map(item => ({
            PRD_FORM_PROC_ITEM_ID: item.ID,
            PRODUTO_ID: item.PRODUTO_ID,
            CENTRO_ESTOQUE_ID: item.CENTRO_ESTOQUE_ID,
            QUANT_CONSUMIDA: Number(item.QUANT_CONSUMIDA || 0),
            PECAS_CONSUMIDAS: Number(item.PECAS_CONSUMIDAS || 0),
            QUANT_SOBRA: Number(item.QUANT_SOBRA_ATUAL || 0),
            PECAS_SOBRA: Number(item.PECAS_SOBRA_ATUAL || 0),
            QUANT_PERDA: Number(item.QUANT_PERDA_ATUAL || 0),
            PECAS_PERDA: Number(item.PECAS_PERDA_ATUAL || 0),
            OBSERVACAO: item.OBSERVACAO || '',
            LOTES: (item.LOTES || []).map(lote => ({
              ESTOQUE_LOTE_ID: lote.ESTOQUE_LOTE_ID,
              LOTE: lote.LOTE,
              QUANT_CONSUMIDA: Number(lote.QUANT_CONSUMIDA || 0),
              PECAS_CONSUMIDAS: Number(lote.PECAS_CONSUMIDAS || 0),
              OBSERVACAO: lote.OBSERVACAO || ''
            }))
          }))
      }
    },
    async salvar() {
      const erro = this.validarFormulario()
      if (erro) {
        this.$bvToast.toast(erro, {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.salvando = true
      try {
        await this.$http.post('/api/protected/producao-formula-apontamento/post', this.montarPayload())
        this.submitAttempted = false
        this.$bvToast.toast('Apontamento parcial registrado com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        await this.carregarTela()
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao registrar apontamento parcial', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.salvando = false
      }
    },
    async abrirModalLotes(item) {
      if (Number(item.QUANT_CONSUMIDA || 0) <= 0 && Number(item.PECAS_CONSUMIDAS || 0) <= 0) {
        this.$bvToast.toast('Informe o consumo do item antes de distribuir os lotes', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.modalLotes.loading = true
      this.modalLotes.itemId = item.ID
      this.modalLotes.item = item
      this.modalLotes.lotes = []

      try {
        const { data } = await axios.get(`/api/protected/producao-formula-apontamento-item/lotes-disponiveis/${item.ID}`)
        this.modalLotes.lotes = (data || []).map(lote => {
          const selecionado = (item.LOTES || []).find(itemLote => Number(itemLote.ESTOQUE_LOTE_ID) === Number(lote.ESTOQUE_LOTE_ID)) || {}
          return {
            ...lote,
            QUANT_CONSUMIDA: Number(selecionado.QUANT_CONSUMIDA || 0),
            PECAS_CONSUMIDAS: Number(selecionado.PECAS_CONSUMIDAS || 0),
            OBSERVACAO: selecionado.OBSERVACAO || ''
          }
        })
        this.$refs['modal-lotes'].show()
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao carregar lotes disponíveis', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.modalLotes.loading = false
      }
    },
    usarValorDoApontamento(loteAtual) {
      if (!this.modalLotes.item || !loteAtual) return

      const quantidadeOutros = this.modalLotes.lotes
        .filter(lote => Number(lote.ESTOQUE_LOTE_ID) !== Number(loteAtual.ESTOQUE_LOTE_ID))
        .reduce((total, lote) => total + Number(lote.QUANT_CONSUMIDA || 0), 0)
      const pecasOutros = this.modalLotes.lotes
        .filter(lote => Number(lote.ESTOQUE_LOTE_ID) !== Number(loteAtual.ESTOQUE_LOTE_ID))
        .reduce((total, lote) => total + Number(lote.PECAS_CONSUMIDAS || 0), 0)

      const restanteQuantidade = Math.max(0, Number(this.modalLotes.item.QUANT_CONSUMIDA || 0) - quantidadeOutros)
      const restantePecas = Math.max(0, Number(this.modalLotes.item.PECAS_CONSUMIDAS || 0) - pecasOutros)

      loteAtual.QUANT_CONSUMIDA = Math.min(restanteQuantidade, Number(loteAtual.QUANT_SALDO || 0))
      loteAtual.PECAS_CONSUMIDAS = Math.min(restantePecas, Number(loteAtual.PECAS_SALDO || 0))
    },
    confirmarLotes() {
      if (!this.modalLotes.item) return

      for (const lote of this.modalLotes.lotes) {
        if (Number(lote.QUANT_CONSUMIDA || 0) > Number(lote.QUANT_SALDO || 0) + 0.0005) {
          this.$bvToast.toast(`Consumo acima do saldo do lote ${lote.LOTE}`, {
            title: 'Aviso',
            variant: 'warning',
            solid: true
          })
          return
        }

        if (Number(lote.PECAS_CONSUMIDAS || 0) > Number(lote.PECAS_SALDO || 0)) {
          this.$bvToast.toast(`Peças acima do saldo do lote ${lote.LOTE}`, {
            title: 'Aviso',
            variant: 'warning',
            solid: true
          })
          return
        }
      }

      if (!this.modalLotesValido) {
        this.$bvToast.toast('A soma dos lotes deve fechar exatamente o consumo informado do item', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      const item = this.getItemById(this.modalLotes.itemId)
      if (item) {
        item.LOTES = this.modalLotes.lotes
          .filter(lote => Number(lote.QUANT_CONSUMIDA || 0) > 0 || Number(lote.PECAS_CONSUMIDAS || 0) > 0)
          .map(lote => ({
            ESTOQUE_LOTE_ID: lote.ESTOQUE_LOTE_ID,
            LOTE: lote.LOTE,
            QUANT_CONSUMIDA: Number(lote.QUANT_CONSUMIDA || 0),
            PECAS_CONSUMIDAS: Number(lote.PECAS_CONSUMIDAS || 0),
            OBSERVACAO: lote.OBSERVACAO || ''
          }))
      }

      this.$bvModal.hide('modal-lotes-producao-formula')
    },
    async abrirModalDetalhes(apontamento) {
      this.modalDetalhes.loading = true
      this.modalDetalhes.apontamento = apontamento
      this.modalDetalhes.itens = []

      try {
        this.modalDetalhes.itens = await this.carregarItensDetalhadosApontamento(apontamento.ID)
        this.$refs['modal-detalhes'].show()
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao carregar itens do apontamento', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.modalDetalhes.loading = false
      }
    },
    podeCancelarApontamento(apontamento) {
      return this.podeEditarApontamento && String(apontamento.OP_STATUS || '1') === '1'
    },
    async cancelarApontamento(apontamento) {
      const confirmar = await this.$bvModal.msgBoxConfirm(
        'O cancelamento vai estornar estoque, lotes e acumulados deste apontamento. Deseja continuar?',
        {
          title: 'Cancelar apontamento',
          okTitle: 'Cancelar apontamento',
          cancelTitle: 'Voltar',
          okVariant: 'danger',
          cancelVariant: 'secondary',
          centered: true
        }
      )

      if (!confirmar) return

      this.isBusy = true
      try {
        await this.$http.post('/api/protected/producao-formula-apontamento/cancelar', {
          id: apontamento.ID,
          login: window.localStorage.getItem('login') || 'SISTEMA'
        })

        this.$bvToast.toast('Apontamento cancelado com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        await this.carregarTela()
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao cancelar apontamento', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isBusy = false
      }
    },
    abrirModalCustoReal(item = null) {
      if (item) {
        this.modalCustoReal.tipoItem = item.PRODUTO_ID ? 'P' : 'M'
        // Converter DATA_LANCAMENTO (Date ou ISO string) para YYYY-MM-DD para o input[type=date]
        let dataStr = item.DATA_LANCAMENTO
        if (dataStr instanceof Date) {
          dataStr = dataStr.toISOString().slice(0, 10)
        } else if (typeof dataStr === 'string' && dataStr.length > 10) {
          dataStr = dataStr.slice(0, 10)
        }
        this.modalCustoReal.item = {
          ...item,
          DATA_LANCAMENTO: dataStr,
          VALOR_TOTAL: item.PRODUTO_ID ? item.VALOR : 0
        }
      } else {
        this.modalCustoReal.tipoItem = 'M'
        this.modalCustoReal.item = {
          ID: null,
          NATUREZA_ID: null,
          DATA_LANCAMENTO: new Date().toISOString().slice(0, 10),
          VALOR: 0,
          PRODUTO_ID: null,
          PRODUTO_CODIGONOME: '',
          QUANTIDADE_EXECUTADA: 0,
          VALOR_UNITARIO_APLICADO: 0,
          VALOR_TOTAL: 0
        }
      }
      this.modalCustoReal.show = true
    },
    resetModalCustoReal() {
      this.modalCustoReal.tipoItem = 'M'
      this.modalCustoReal.enviado = false
      this.modalCustoReal.item = {
        ID: null,
        NATUREZA_ID: null,
        DATA_LANCAMENTO: new Date().toISOString().slice(0, 10),
        VALOR: 0,
        PRODUTO_ID: null,
        PRODUTO_CODIGONOME: '',
        QUANTIDADE_EXECUTADA: 0,
        VALOR_UNITARIO_APLICADO: 0,
        VALOR_TOTAL: 0
      }
      this.modalCustoReal.loading = false
      this.modalCustoReal.show = false
    },
    setProdutoId(id) {
      this.modalCustoReal.item.PRODUTO_ID = id
    },
    setProdutoNome(nome) {
      this.modalCustoReal.item.PRODUTO_CODIGONOME = nome || ''
    },
    setProdutoCusto(custo) {
      if (custo && Number(custo) > 0) {
        this.modalCustoReal.item.VALOR_UNITARIO_APLICADO = Number(custo)
      }
    },
    selecionarProdutoModalCustoReal(produto) {
      this.modalCustoReal.item.PRODUTO_ID = produto.COD_PRO || produto.ID || produto.CODIGO
      this.modalCustoReal.item.PRODUTO_CODIGONOME = `${produto.COD_PRO || produto.CODIGO} - ${produto.DESCRICAO}`
      this.modalCustoReal.item.VALOR_UNITARIO_APLICADO = Number(produto.VL_CUSTO || 0)
    },
    limparProdutoModalCustoReal() {
      this.modalCustoReal.item.PRODUTO_ID = null
      this.modalCustoReal.item.PRODUTO_CODIGONOME = ''
      this.modalCustoReal.item.VALOR_UNITARIO_APLICADO = 0
    },
    async salvarCustoReal() {
      this.modalCustoReal.enviado = true

      // Validação unificada por tipo
      if (!this.modalCustoReal.item.NATUREZA_ID || !this.modalCustoReal.item.DATA_LANCAMENTO) {
        return
      }
      if (this.modalCustoReal.tipoItem === 'P') {
        if (!this.modalCustoReal.item.PRODUTO_ID || !(Number(this.modalCustoReal.item.QUANTIDADE_EXECUTADA) > 0)) {
          return
        }
      } else {
        if (!(Number(this.valorTotalModalCustoReal) > 0)) {
          return
        }
      }

      this.modalCustoReal.loading = true
      try {
        const url = '/api/protected/producao-formula-apontamento/custo-real/post'

        const payloadItem = {
          ...this.modalCustoReal.item,
          PRD_FORM_PROC_ID: this.processo.ID
        }

        if (this.modalCustoReal.tipoItem === 'P') {
          payloadItem.VALOR_TOTAL = this.valorTotalModalCustoReal
          payloadItem.VALOR = this.valorTotalModalCustoReal // Compatibilidade
        } else {
          payloadItem.PRODUTO_ID = null
          payloadItem.QUANTIDADE_EXECUTADA = null
          payloadItem.VALOR_UNITARIO_APLICADO = null
          payloadItem.VALOR_TOTAL = null
        }

        await this.$http.post(url, {
          registro: payloadItem,
          login: window.localStorage.getItem('login') || 'SISTEMA'
        })

        this.$bvToast.toast('Custo real salvo com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        // Recarrega a lista ANTES de fechar o modal para garantir que os dados estão atualizados
        await this.carregarCustosReais()
        // Fecha o modal via v-model — o evento @hidden se encarrega de chamar resetModalCustoReal()
        this.modalCustoReal.show = false
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao salvar custo real', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.modalCustoReal.loading = false
      }
    },
    async excluirCustoReal(id) {
      const confirmar = await this.$bvModal.msgBoxConfirm('Deseja realmente excluir este custo operacional?', {
        title: 'Excluir custo real',
        okTitle: 'Sim, excluir',
        cancelTitle: 'Cancelar',
        okVariant: 'danger',
        cancelVariant: 'secondary',
        centered: true
      })

      if (!confirmar) return

      try {
        await this.$http.delete(`/api/protected/producao-formula-apontamento/custo-real/delete/${id}`)

        this.$bvToast.toast('Custo real excluído com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        await this.carregarCustosReais()
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao excluir custo real', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },
    async carregarCustosReais() {
      if (!this.processo || !this.processo.ID) return
      this.carregandoCustos = true
      try {
        const { data } = await this.$http.get(`/api/protected/producao-formula-apontamento/custo-real/${this.processo.ID}`)
        this.custosReais = data || []
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao carregar custos reais', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.carregandoCustos = false
      }
    },
    async finalizarProcesso() {
      const confirmar = await this.$bvModal.msgBoxConfirm(
        'O processo será encerrado e ficará somente leitura. Novos apontamentos e cancelamentos não serão mais permitidos. Deseja finalizar?',
        {
          title: 'Finalizar processo',
          okTitle: 'Finalizar',
          cancelTitle: 'Voltar',
          okVariant: 'warning',
          cancelVariant: 'secondary',
          centered: true
        }
      )

      if (!confirmar) return

      this.isBusy = true
      try {
        await this.$http.post('/api/protected/producao-formula-processo/finalizar', {
          id: this.processo.ID,
          login: window.localStorage.getItem('login') || 'SISTEMA'
        })

        this.$bvToast.toast('Processo finalizado com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        await this.carregarTela()
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao finalizar processo', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isBusy = false
      }
    }
  }
}
</script>

<style scoped>
.table-finance tbody td {
  vertical-align: middle !important;
}

.nested-lotes-table {
  background: #fff;
}

.detalhes-summary-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 0.75rem;
}

.detalhes-summary-item {
  border: 1px solid rgba(40, 60, 80, 0.14);
  background: #e9eeef;
  border-radius: 0.85rem;
  padding: 0.85rem 0.9rem;
  text-align: center;
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.04);
}

.detalhes-summary-item small {
  display: block;
  color: #6c757d;
  margin-bottom: 0.15rem;
}

.detalhes-summary-item strong {
  font-size: 1rem;
}

.detalhe-item-card {
  border: 1px solid rgba(98, 122, 145, 0.18);
  border-radius: 0.9rem;
  background: #edf2f5;
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.05);
  padding: 1rem;
}

.detalhe-item-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.detalhe-item-badge-wrap {
  flex-shrink: 0;
}

.detalhe-item-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.detalhe-metric-box {
  background: #f4f7fa;
  border: 1px solid rgba(98, 122, 145, 0.12);
  border-radius: 0.75rem;
  padding: 0.8rem 0.85rem;
}

.detalhe-metric-box small {
  display: block;
  color: #6c757d;
  margin-bottom: 0.2rem;
}

.detalhe-metric-box strong {
  display: block;
  font-size: 1rem;
}

.detalhe-lotes-box {
  background: #e2ecf5;
  border: 1px solid rgba(43, 109, 176, 0.24);
  border-radius: 0.8rem;
  padding: 0.9rem;
  box-shadow: 0 4px 12px rgba(43, 109, 176, 0.08);
}

.detalhe-sem-lote {
  background: #eceff1;
  border: 1px dashed rgba(108, 117, 125, 0.45);
  color: #6c757d;
  border-radius: 0.75rem;
  padding: 0.85rem 0.9rem;
}

.section-icon {
  width: 40px;
  height: 40px;
}

.summary-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.85rem;
  padding: 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
}

.modal-header-premium {
  border-bottom: none;
}

.header-icon-wrapper {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1.5rem;
}

.bg-white-10 {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.modal-label-premium {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 0.4rem;
  display: block;
}

.shadow-xs {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.field-help-text {
  display: block;
  margin-top: 0.45rem;
  color: #6c757d;
  font-size: 0.76rem;
  line-height: 1.35;
}

.rateio-hint {
  border: 1px solid rgba(13, 110, 253, 0.18);
  background: rgba(13, 110, 253, 0.06);
  color: #345;
  border-radius: 0.75rem;
  padding: 0.75rem 0.9rem;
  font-size: 0.9rem;
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 0.75rem;
}

.summary-strip-item {
  min-width: 120px;
  padding: 0.8rem 0.9rem;
  border-radius: 0.8rem;
  background: #f8f9fa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;
}

.summary-strip-item small {
  display: block;
  color: #6c757d;
  margin-bottom: 0.2rem;
}

.summary-strip-item strong {
  font-size: 1rem;
}

.linha-controla-lote {
  background: rgba(255, 193, 7, 0.05);
}

.linha-rateio-pendente {
  background: rgba(220, 53, 69, 0.08) !important;
}

.linha-rateio-pendente td {
  box-shadow: inset 0 1px 0 rgba(220, 53, 69, 0.12), inset 0 -1px 0 rgba(220, 53, 69, 0.12);
}

.gap-1 {
  gap: 0.35rem;
}

@media (max-width: 991.98px) {
  .summary-strip {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .detalhes-summary-strip {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .detalhe-item-metrics {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
}

@media (max-width: 575.98px) {
  .summary-strip {
    grid-template-columns: 1fr;
  }

  .detalhes-summary-strip,
  .detalhe-item-metrics {
    grid-template-columns: 1fr;
  }

  .detalhe-item-header {
    flex-direction: column;
  }
}

.btn-soft-danger {
  background-color: rgba(248, 108, 107, 0.1);
  color: #f86c6b;
  border: none;
}

.btn-soft-danger:hover {
  background-color: #f86c6b;
  color: #fff;
}

.linha-apontamento-cancelado {
  background-color: rgba(220, 53, 69, 0.05) !important;
}

.linha-apontamento-cancelado td {
  color: #8a9196 !important;
  font-style: italic;
}

.custom-switch-premium .custom-control-input:checked ~ .custom-control-label::before {
  border-color: #f86c6b;
  background-color: #f86c6b;
}

.custom-switch-premium {
  transform: scale(0.8);
  transform-origin: right center;
}

.gap-2 {
  gap: 0.5rem !important;
}

.sobra-produto-wrapper {
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.bg-warning-soft {
  background-color: rgba(249, 177, 21, 0.08) !important;
}
</style>



// WEBPACK FOOTER //
// src/views/producao/producao-formula-apontamento/ProducaoFormulaApontamentoCadastro.vue